import { __publicField, useTheme } from './chunk-ZE6ENRWD.js';
import React2, { forwardRef, useState, useEffect, createContext, useMemo, useContext, useRef, useCallback, Component } from 'react';
import { clsx } from 'clsx';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

function cn(...inputs) {
  return clsx(inputs);
}
var buttonVariants = {
  primary: "bg-[var(--color-brand-primary)] text-white hover:opacity-90 active:opacity-80",
  secondary: "bg-[var(--color-brand-secondary)] text-white hover:opacity-90 active:opacity-80",
  ghost: "bg-transparent text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)] active:bg-[var(--color-neutral-200)]",
  danger: "bg-[var(--color-brand-danger)] text-white hover:opacity-90 active:opacity-80"
};
var buttonSizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg"
};
var Button = forwardRef(
  ({
    variant = "primary",
    size = "md",
    disabled = false,
    loading = false,
    icon,
    children,
    fullWidth = false,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        disabled: disabled || loading,
        className: cn(
          // Base styles
          "inline-flex items-center justify-center gap-2",
          "font-medium rounded-[var(--radius-md)]",
          "transition-all duration-[var(--transition-duration-base)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-offset-2",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Variant styles
          buttonVariants[variant],
          // Size styles
          buttonSizes[size],
          // Full width
          fullWidth && "w-full",
          // Custom className
          className
        ),
        ...props,
        children: [
          loading && /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "animate-spin h-4 w-4",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              "aria-hidden": "true",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          icon && !loading && icon,
          children
        ]
      }
    );
  }
);
Button.displayName = "Button";
var cardVariants = {
  elevated: "bg-[var(--color-neutral-100)] shadow-[var(--shadow-md)] border border-[var(--color-neutral-200)]",
  outlined: "bg-[var(--color-neutral-100)] border-2 border-[var(--color-neutral-300)]",
  glass: "backdrop-blur-sm border border-[var(--color-neutral-200)]"
};
var cardPadding = {
  none: "p-0",
  sm: "p-3",
  md: "p-4",
  lg: "p-6"
};
var Card = forwardRef(
  ({
    variant = "elevated",
    padding = "md",
    hoverable = false,
    children,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          // Base styles
          "rounded-[var(--radius-lg)]",
          "transition-all duration-[var(--transition-duration-base)]",
          // Variant styles
          cardVariants[variant],
          // Padding
          cardPadding[padding],
          // Hoverable
          hoverable && "hover:shadow-[var(--shadow-lg)] cursor-pointer",
          // Custom className
          className
        ),
        ...props,
        children
      }
    );
  }
);
Card.displayName = "Card";
var CardHeader = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("mb-4", className),
        ...props,
        children
      }
    );
  }
);
CardHeader.displayName = "CardHeader";
var CardBody = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("", className),
        ...props,
        children
      }
    );
  }
);
CardBody.displayName = "CardBody";
var CardFooter = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("mt-4 flex gap-2", className),
        ...props,
        children
      }
    );
  }
);
CardFooter.displayName = "CardFooter";
var inputSizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-5 py-3 text-lg"
};
var Input = forwardRef(
  ({
    label,
    error,
    helperText,
    required = false,
    size = "md",
    icon,
    fullWidth = false,
    className,
    id,
    ...props
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", fullWidth && "w-full"), children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: inputId,
          className: cn(
            "text-sm font-medium text-[var(--color-neutral-700)]",
            required && 'after:content-["*"] after:ml-0.5 after:text-[var(--color-brand-danger)]'
          ),
          children: label
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        icon && /* @__PURE__ */ jsx("div", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-400)]", children: icon }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            id: inputId,
            className: cn(
              // Base styles
              "w-full rounded-[var(--radius-md)]",
              "border border-[var(--color-neutral-300)]",
              "bg-[var(--color-neutral-100)] text-[var(--color-neutral-900)]",
              "transition-colors duration-[var(--transition-duration-base)]",
              "placeholder:text-[var(--color-neutral-400)]",
              // Focus styles
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent",
              // Error styles
              error && "border-[var(--color-brand-danger)] focus:ring-[var(--color-brand-danger)]",
              // Disabled styles
              "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50",
              // Size
              inputSizes[size],
              // Icon padding
              icon && "pl-10",
              // Custom className
              className
            ),
            "aria-invalid": !!error,
            "aria-describedby": error ? `${inputId}-error` : helperText ? `${inputId}-helper` : void 0,
            required,
            ...props
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${inputId}-error`,
          className: "text-sm text-[var(--color-brand-danger)]",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${inputId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)]",
          children: helperText
        }
      )
    ] });
  }
);
Input.displayName = "Input";
var badgeVariants = {
  default: "bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)] border-[var(--color-neutral-300)]",
  primary: "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)] border-[var(--color-brand-primary)]/20",
  success: "bg-[var(--color-semantic-success)]/10 text-[var(--color-semantic-success)] border-[var(--color-semantic-success)]/20",
  warning: "bg-[var(--color-brand-warning)]/10 text-[var(--color-brand-warning)] border-[var(--color-brand-warning)]/20",
  danger: "bg-[var(--color-brand-danger)]/10 text-[var(--color-brand-danger)] border-[var(--color-brand-danger)]/20",
  info: "bg-[var(--color-brand-info)]/10 text-[var(--color-brand-info)] border-[var(--color-brand-info)]/20"
};
var badgeSizes = {
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-sm",
  lg: "px-3 py-1.5 text-base"
};
var Badge = forwardRef(
  ({
    variant = "default",
    size = "md",
    dot = false,
    children,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "span",
      {
        ref,
        className: cn(
          // Base styles
          "inline-flex items-center gap-1.5",
          "font-medium rounded-full border",
          "transition-colors duration-[var(--transition-duration-base)]",
          // Variant styles
          badgeVariants[variant],
          // Size styles
          badgeSizes[size],
          // Custom className
          className
        ),
        ...props,
        children: [
          dot && /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "w-1.5 h-1.5 rounded-full",
                variant === "default" && "bg-[var(--color-neutral-500)]",
                variant === "primary" && "bg-[var(--color-brand-primary)]",
                variant === "success" && "bg-[var(--color-semantic-success)]",
                variant === "warning" && "bg-[var(--color-brand-warning)]",
                variant === "danger" && "bg-[var(--color-brand-danger)]",
                variant === "info" && "bg-[var(--color-brand-info)]"
              )
            }
          ),
          children
        ]
      }
    );
  }
);
Badge.displayName = "Badge";
var avatarSizes = {
  xs: "w-6 h-6 text-xs",
  sm: "w-8 h-8 text-sm",
  md: "w-10 h-10 text-base",
  lg: "w-12 h-12 text-lg",
  xl: "w-16 h-16 text-xl"
};
var statusColors = {
  online: "bg-[var(--color-semantic-success)]",
  offline: "bg-[var(--color-neutral-400)]",
  away: "bg-[var(--color-brand-warning)]",
  busy: "bg-[var(--color-brand-danger)]"
};
var Avatar = forwardRef(
  ({
    src,
    alt,
    fallback,
    size = "md",
    shape = "circle",
    backgroundColor,
    status,
    className,
    ...props
  }, ref) => {
    const [imageError, setImageError] = useState(false);
    const showFallback = !src || imageError;
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("relative inline-block", className), ...props, children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "inline-flex items-center justify-center",
            "overflow-hidden",
            "font-medium",
            // Size
            avatarSizes[size],
            // Shape
            shape === "circle" ? "rounded-full" : "rounded-[var(--radius-md)]",
            // Background (only if no custom backgroundColor)
            showFallback && !backgroundColor && "bg-[var(--color-brand-primary)]",
            showFallback && "text-white"
          ),
          style: backgroundColor ? { backgroundColor } : void 0,
          children: !showFallback ? /* @__PURE__ */ jsx(
            "img",
            {
              src,
              alt: alt || "Avatar",
              className: "w-full h-full object-cover",
              onError: () => setImageError(true)
            }
          ) : /* @__PURE__ */ jsx("span", { className: "select-none", children: fallback || alt?.charAt(0).toUpperCase() || "?" })
        }
      ),
      status && /* @__PURE__ */ jsx(
        "span",
        {
          className: cn(
            "absolute bottom-0 right-0",
            "w-3 h-3 rounded-full border-2 border-white",
            statusColors[status]
          ),
          "aria-label": `Status: ${status}`
        }
      )
    ] });
  }
);
Avatar.displayName = "Avatar";
var modalSizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full mx-4"
};
var Modal = forwardRef(
  ({
    open,
    onClose,
    title,
    children,
    size = "md",
    showCloseButton = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    className,
    ...props
  }, ref) => {
    useEffect(() => {
      if (!open || !closeOnEscape) return;
      const handleEscape = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, closeOnEscape, onClose]);
    useEffect(() => {
      if (open) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return () => {
        document.body.style.overflow = "";
      };
    }, [open]);
    if (!open) return null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: "fixed inset-0 z-[var(--z-index-modal)] flex items-center justify-center",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? "modal-title" : void 0,
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "absolute inset-0 bg-black/50 backdrop-blur-sm",
              onClick: closeOnBackdropClick ? onClose : void 0,
              "aria-hidden": "true"
            }
          ),
          /* @__PURE__ */ jsxs(
            "div",
            {
              ref,
              className: cn(
                "relative w-full",
                "bg-[var(--color-neutral-100)] rounded-[var(--radius-xl)]",
                "shadow-[var(--shadow-xl)]",
                "max-h-[90vh] overflow-y-auto",
                "animate-in fade-in zoom-in-95 duration-200",
                // Size
                modalSizes[size],
                // Custom className
                className
              ),
              ...props,
              children: [
                (title || showCloseButton) && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between p-6 border-b border-[var(--color-neutral-200)]", children: [
                  title && /* @__PURE__ */ jsx(
                    "h2",
                    {
                      id: "modal-title",
                      className: "text-xl font-semibold text-[var(--color-neutral-900)]",
                      children: title
                    }
                  ),
                  showCloseButton && /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      onClick: onClose,
                      className: cn(
                        "p-2 rounded-[var(--radius-md)]",
                        "text-[var(--color-neutral-500)] hover:text-[var(--color-neutral-700)]",
                        "hover:bg-[var(--color-neutral-100)]",
                        "transition-colors duration-[var(--transition-duration-base)]",
                        "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)]",
                        !title && "ml-auto"
                      ),
                      "aria-label": "Close modal",
                      children: /* @__PURE__ */ jsx(
                        "svg",
                        {
                          className: "w-5 h-5",
                          fill: "none",
                          viewBox: "0 0 24 24",
                          stroke: "currentColor",
                          children: /* @__PURE__ */ jsx(
                            "path",
                            {
                              strokeLinecap: "round",
                              strokeLinejoin: "round",
                              strokeWidth: 2,
                              d: "M6 18L18 6M6 6l12 12"
                            }
                          )
                        }
                      )
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx("div", { className: "p-6", children })
              ]
            }
          )
        ]
      }
    );
  }
);
Modal.displayName = "Modal";
var ModalFooter = forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex items-center justify-end gap-2",
          "pt-4 mt-4 border-t border-[var(--color-neutral-200)]",
          className
        ),
        ...props,
        children
      }
    );
  }
);
ModalFooter.displayName = "ModalFooter";
var spinnerSizes = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8",
  xl: "w-12 h-12"
};
var spinnerColors = {
  primary: "text-[var(--color-brand-primary)]",
  secondary: "text-[var(--color-brand-secondary)]",
  white: "text-white",
  current: "text-current"
};
var Spinner = forwardRef(
  ({ size = "md", color = "primary", className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        role: "status",
        "aria-label": "Loading",
        className: cn("inline-block", className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: cn(
                "animate-spin",
                spinnerSizes[size],
                spinnerColors[color]
              ),
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading..." })
        ]
      }
    );
  }
);
Spinner.displayName = "Spinner";
var textareaSizes = {
  sm: "px-3 py-1.5 text-sm min-h-[80px]",
  md: "px-4 py-2 text-base min-h-[120px]",
  lg: "px-5 py-3 text-lg min-h-[160px]"
};
var resizeClasses = {
  none: "resize-none",
  vertical: "resize-y",
  horizontal: "resize-x",
  both: "resize"
};
var TextArea = forwardRef(
  ({
    label,
    error,
    helperText,
    required = false,
    size = "md",
    fullWidth = false,
    resize = "vertical",
    className,
    id,
    ...props
  }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", fullWidth && "w-full"), children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: textareaId,
          className: cn(
            "text-sm font-medium text-[var(--color-neutral-700)]",
            required && 'after:content-["*"] after:ml-0.5 after:text-[var(--color-brand-danger)]'
          ),
          children: label
        }
      ),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          ref,
          id: textareaId,
          className: cn(
            // Base styles
            "w-full rounded-[var(--radius-md)]",
            "border border-[var(--color-neutral-300)]",
            "bg-[var(--color-neutral-50)] text-[var(--color-neutral-900)]",
            "transition-colors duration-[var(--transition-duration-base)]",
            "placeholder:text-[var(--color-neutral-400)]",
            // Focus styles
            "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent",
            // Error styles
            error && "border-[var(--color-brand-danger)] focus:ring-[var(--color-brand-danger)]",
            // Disabled styles
            "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50",
            // Size
            textareaSizes[size],
            // Resize
            resizeClasses[resize],
            // Custom className
            className
          ),
          "aria-invalid": !!error,
          "aria-describedby": error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : void 0,
          required,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${textareaId}-error`,
          className: "text-sm text-[var(--color-brand-danger)]",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${textareaId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)]",
          children: helperText
        }
      )
    ] });
  }
);
TextArea.displayName = "TextArea";
var selectSizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-5 py-3 text-lg"
};
var Select = forwardRef(
  ({
    label,
    error,
    helperText,
    required = false,
    size = "md",
    fullWidth = false,
    options,
    placeholder,
    className,
    id,
    children,
    ...props
  }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", fullWidth && "w-full"), children: [
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: selectId,
          className: cn(
            "text-sm font-medium text-[var(--color-neutral-700)]",
            required && 'after:content-["*"] after:ml-0.5 after:text-[var(--color-brand-danger)]'
          ),
          children: label
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsxs(
          "select",
          {
            ref,
            id: selectId,
            className: cn(
              // Base styles
              "w-full rounded-[var(--radius-md)]",
              "border border-[var(--color-neutral-300)]",
              "bg-[var(--color-neutral-50)] text-[var(--color-neutral-900)]",
              "transition-colors duration-[var(--transition-duration-base)]",
              // Appearance
              "appearance-none",
              "cursor-pointer",
              // Focus styles
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent",
              // Error styles
              error && "border-[var(--color-brand-danger)] focus:ring-[var(--color-brand-danger)]",
              // Disabled styles
              "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50",
              // Size
              selectSizes[size],
              // Padding for icon
              "pr-10",
              // Custom className
              className
            ),
            "aria-invalid": !!error,
            "aria-describedby": error ? `${selectId}-error` : helperText ? `${selectId}-helper` : void 0,
            required,
            ...props,
            children: [
              placeholder && /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: placeholder }),
              options ? options.map((option) => /* @__PURE__ */ jsx("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)) : children
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-neutral-400)]", children: /* @__PURE__ */ jsx(
          "svg",
          {
            width: "16",
            height: "16",
            viewBox: "0 0 16 16",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            children: /* @__PURE__ */ jsx(
              "path",
              {
                d: "M4 6L8 10L12 6",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round"
              }
            )
          }
        ) })
      ] }),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${selectId}-error`,
          className: "text-sm text-[var(--color-brand-danger)]",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${selectId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)]",
          children: helperText
        }
      )
    ] });
  }
);
Select.displayName = "Select";
var checkboxSizes = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6"
};
var labelSizes = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg"
};
var Checkbox = forwardRef(
  ({
    label,
    error,
    helperText,
    size = "md",
    indeterminate = false,
    className,
    id,
    ...props
  }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            type: "checkbox",
            id: checkboxId,
            className: cn(
              // Base styles
              "rounded-[var(--radius-sm)]",
              "border-2 border-[var(--color-neutral-300)]",
              "bg-[var(--color-neutral-100)]",
              "transition-colors duration-[var(--transition-duration-base)]",
              "cursor-pointer",
              // Focus styles
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-offset-2",
              // Checked styles
              "checked:bg-[var(--color-brand-primary)] checked:border-[var(--color-brand-primary)]",
              // Error styles
              error && "border-[var(--color-brand-danger)]",
              // Disabled styles
              "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50",
              // Indeterminate styles
              indeterminate && "bg-[var(--color-brand-primary)] border-[var(--color-brand-primary)]",
              // Size
              checkboxSizes[size],
              // Custom className
              className
            ),
            "aria-invalid": !!error,
            "aria-describedby": error ? `${checkboxId}-error` : helperText ? `${checkboxId}-helper` : void 0,
            ...props
          }
        ),
        label && /* @__PURE__ */ jsx(
          "label",
          {
            htmlFor: checkboxId,
            className: cn(
              "font-medium text-[var(--color-neutral-700)] cursor-pointer select-none",
              labelSizes[size]
            ),
            children: label
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${checkboxId}-error`,
          className: "text-sm text-[var(--color-brand-danger)] ml-7",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${checkboxId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)] ml-7",
          children: helperText
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";
var radioSizes = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6"
};
var labelSizes2 = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg"
};
var Radio = forwardRef(
  ({
    label,
    error,
    helperText,
    size = "md",
    className,
    id,
    ...props
  }, ref) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            type: "radio",
            id: radioId,
            className: cn(
              // Base styles
              "rounded-full",
              "border-2 border-[var(--color-neutral-300)]",
              "bg-[var(--color-neutral-100)]",
              "transition-colors duration-[var(--transition-duration-base)]",
              "cursor-pointer",
              // Focus styles
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-offset-2",
              // Checked styles
              "checked:bg-[var(--color-brand-primary)] checked:border-[var(--color-brand-primary)]",
              'checked:before:content-[""] checked:before:block checked:before:w-full checked:before:h-full',
              "checked:before:rounded-full checked:before:bg-white checked:before:scale-50",
              // Error styles
              error && "border-[var(--color-brand-danger)]",
              // Disabled styles
              "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50",
              // Size
              radioSizes[size],
              // Custom className
              className
            ),
            "aria-invalid": !!error,
            "aria-describedby": error ? `${radioId}-error` : helperText ? `${radioId}-helper` : void 0,
            ...props
          }
        ),
        label && /* @__PURE__ */ jsx(
          "label",
          {
            htmlFor: radioId,
            className: cn(
              "font-medium text-[var(--color-neutral-700)] cursor-pointer select-none",
              labelSizes2[size]
            ),
            children: label
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${radioId}-error`,
          className: "text-sm text-[var(--color-brand-danger)] ml-7",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${radioId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)] ml-7",
          children: helperText
        }
      )
    ] });
  }
);
Radio.displayName = "Radio";
var switchSizes = {
  sm: {
    container: "h-5 w-9",
    thumb: "h-4 w-4",
    translate: "translate-x-4"
  },
  md: {
    container: "h-6 w-11",
    thumb: "h-5 w-5",
    translate: "translate-x-5"
  },
  lg: {
    container: "h-7 w-14",
    thumb: "h-6 w-6",
    translate: "translate-x-7"
  }
};
var labelSizes3 = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg"
};
var Switch = forwardRef(
  ({
    label,
    error,
    helperText,
    size = "md",
    labelPosition = "right",
    className,
    id,
    checked,
    ...props
  }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "flex items-center gap-3",
            labelPosition === "left" && "flex-row-reverse justify-end"
          ),
          children: [
            /* @__PURE__ */ jsxs("div", { className: "relative inline-block", children: [
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref,
                  type: "checkbox",
                  id: switchId,
                  className: "sr-only",
                  checked,
                  "aria-invalid": !!error,
                  "aria-describedby": error ? `${switchId}-error` : helperText ? `${switchId}-helper` : void 0,
                  ...props
                }
              ),
              /* @__PURE__ */ jsx(
                "label",
                {
                  htmlFor: switchId,
                  className: cn(
                    // Container
                    "block relative rounded-full cursor-pointer transition-colors duration-[var(--transition-duration-base)]",
                    "focus-within:ring-2 focus-within:ring-[var(--color-brand-primary)] focus-within:ring-offset-2",
                    // Background colors
                    checked ? "bg-[var(--color-brand-primary)]" : "bg-[var(--color-neutral-300)]",
                    // Error styles
                    error && "bg-[var(--color-brand-danger)]",
                    // Disabled styles
                    props.disabled && "opacity-50 cursor-not-allowed",
                    // Size
                    switchSizes[size].container
                  ),
                  children: /* @__PURE__ */ jsx(
                    "span",
                    {
                      className: cn(
                        // Thumb
                        "absolute top-0.5 left-0.5 bg-white rounded-full transition-transform duration-[var(--transition-duration-base)]",
                        // Size
                        switchSizes[size].thumb,
                        // Translation when checked
                        checked && switchSizes[size].translate
                      )
                    }
                  )
                }
              )
            ] }),
            label && /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "font-medium text-[var(--color-neutral-700)] select-none",
                  labelSizes3[size],
                  props.disabled && "opacity-50"
                ),
                children: label
              }
            )
          ]
        }
      ),
      error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${switchId}-error`,
          className: "text-sm text-[var(--color-brand-danger)]",
          role: "alert",
          children: error
        }
      ),
      helperText && !error && /* @__PURE__ */ jsx(
        "span",
        {
          id: `${switchId}-helper`,
          className: "text-sm text-[var(--color-neutral-600)]",
          children: helperText
        }
      )
    ] });
  }
);
Switch.displayName = "Switch";
function Skeleton({
  width = "100%",
  height = "1rem",
  variant = "rectangle",
  animate = true,
  className,
  style,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "bg-[var(--color-neutral-200)]",
        animate && "animate-pulse",
        variant === "circle" && "rounded-full",
        variant === "rectangle" && "rounded-[var(--radius-md)]",
        variant === "text" && "rounded-[var(--radius-sm)]",
        className
      ),
      style: {
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        ...style
      },
      role: "status",
      "aria-label": "Loading",
      ...props,
      children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading..." })
    }
  );
}
Skeleton.displayName = "Skeleton";
var progressSizes = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3"
};
var progressVariants = {
  primary: "bg-[var(--color-brand-primary)]",
  secondary: "bg-[var(--color-brand-secondary)]",
  success: "bg-[var(--color-semantic-success)]",
  warning: "bg-[var(--color-semantic-warning)]",
  danger: "bg-[var(--color-semantic-error)]"
};
function ProgressBar({
  value,
  max = 100,
  size = "md",
  variant = "primary",
  showLabel = false,
  animated = true,
  label,
  className,
  ...props
}) {
  const percentage = Math.min(Math.max(value / max * 100, 0), 100);
  return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
    (label || showLabel) && /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-1", children: [
      label && /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-[var(--color-neutral-700)]", children: label }),
      showLabel && /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium text-[var(--color-neutral-700)]", children: [
        Math.round(percentage),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "w-full bg-[var(--color-neutral-200)] rounded-full overflow-hidden",
          progressSizes[size],
          className
        ),
        role: "progressbar",
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max,
        ...props,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "h-full rounded-full transition-all duration-[var(--transition-duration-slow)]",
              animated && "ease-out",
              progressVariants[variant]
            ),
            style: { width: `${percentage}%` }
          }
        )
      }
    )
  ] });
}
ProgressBar.displayName = "ProgressBar";
var positionClasses = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2"
};
var arrowClasses = {
  top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-[var(--color-neutral-900)]",
  bottom: "bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-[var(--color-neutral-900)]",
  left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-[var(--color-neutral-900)]",
  right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-[var(--color-neutral-900)]"
};
function Tooltip({
  content,
  children,
  position = "top",
  delay = 200,
  className
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const handleMouseEnter = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };
  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsVisible(false);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "relative inline-block",
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [
        children,
        isVisible && /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "absolute z-50 px-3 py-2",
              "bg-[var(--color-neutral-900)] text-white text-sm rounded-[var(--radius-md)]",
              "shadow-[var(--shadow-lg)]",
              "whitespace-nowrap",
              "pointer-events-none",
              positionClasses[position],
              className
            ),
            role: "tooltip",
            children: [
              content,
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: cn(
                    "absolute w-0 h-0",
                    "border-4",
                    arrowClasses[position]
                  )
                }
              )
            ]
          }
        )
      ]
    }
  );
}
Tooltip.displayName = "Tooltip";
var TabsContext = createContext(null);
function Tabs({
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  variant = "line",
  className,
  ...props
}) {
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const activeTab = controlledValue !== void 0 ? controlledValue : internalValue;
  const setActiveTab = (newValue) => {
    if (controlledValue === void 0) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: { activeTab, setActiveTab }, children: /* @__PURE__ */ jsx("div", { className: cn("w-full", className), "data-variant": variant, ...props, children }) });
}
function TabsList({ children, className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "tablist",
      className: cn(
        "flex border-b border-[var(--color-neutral-200)]",
        className
      ),
      ...props,
      children
    }
  );
}
function TabsTrigger({ value, children, disabled, className, onClick, ...props }) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("TabsTrigger must be used within Tabs");
  }
  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;
  const handleClick = (e) => {
    if (!disabled) {
      setActiveTab(value);
      onClick?.(e);
    }
  };
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      role: "tab",
      "aria-selected": isActive,
      "aria-controls": `panel-${value}`,
      disabled,
      onClick: handleClick,
      className: cn(
        "px-4 py-2 text-sm font-medium transition-colors relative",
        "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-offset-2",
        isActive ? "text-[var(--color-brand-primary)]" : "text-[var(--color-neutral-600)] hover:text-[var(--color-neutral-900)]",
        disabled && "opacity-50 cursor-not-allowed",
        className
      ),
      ...props,
      children: [
        children,
        isActive && /* @__PURE__ */ jsx("span", { className: "absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--color-brand-primary)]" })
      ]
    }
  );
}
function TabsContent({ value, children, className, ...props }) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("TabsContent must be used within Tabs");
  }
  const { activeTab } = context;
  const isActive = activeTab === value;
  if (!isActive) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "tabpanel",
      id: `panel-${value}`,
      "aria-labelledby": `tab-${value}`,
      className: cn("mt-4", className),
      ...props,
      children
    }
  );
}
Tabs.displayName = "Tabs";
TabsList.displayName = "TabsList";
TabsTrigger.displayName = "TabsTrigger";
TabsContent.displayName = "TabsContent";
var AccordionContext = createContext(null);
function Accordion({
  type = "single",
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className,
  ...props
}) {
  const [internalValue, setInternalValue] = useState(() => {
    if (defaultValue) {
      return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
    }
    return [];
  });
  const openItems = controlledValue !== void 0 ? Array.isArray(controlledValue) ? controlledValue : [controlledValue] : internalValue;
  const toggleItem = (value) => {
    let newValue;
    if (type === "single") {
      newValue = openItems.includes(value) ? [] : [value];
    } else {
      newValue = openItems.includes(value) ? openItems.filter((item) => item !== value) : [...openItems, value];
    }
    if (controlledValue === void 0) {
      setInternalValue(newValue);
    }
    onValueChange?.(type === "single" ? newValue[0] || "" : newValue);
  };
  return /* @__PURE__ */ jsx(AccordionContext.Provider, { value: { openItems, toggleItem, type }, children: /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), ...props, children }) });
}
function AccordionItem({ value, disabled, children, className, ...props }) {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionItem must be used within Accordion");
  }
  const { openItems } = context;
  const isOpen = openItems.includes(value);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "border border-[var(--color-neutral-200)] rounded-[var(--radius-lg)] overflow-hidden",
        className
      ),
      "data-state": isOpen ? "open" : "closed",
      "data-disabled": disabled,
      ...props,
      children: /* @__PURE__ */ jsx(AccordionItemContext.Provider, { value: { value, disabled, isOpen }, children })
    }
  );
}
var AccordionItemContext = createContext(null);
function AccordionTrigger({ children, className, onClick, ...props }) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);
  if (!context || !itemContext) {
    throw new Error("AccordionTrigger must be used within AccordionItem");
  }
  const { toggleItem } = context;
  const { value, disabled, isOpen } = itemContext;
  const handleClick = (e) => {
    if (!disabled) {
      toggleItem(value);
      onClick?.(e);
    }
  };
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: handleClick,
      disabled,
      "aria-expanded": isOpen,
      className: cn(
        "flex items-center justify-between w-full px-4 py-3 text-left font-medium",
        "text-[var(--color-neutral-900)] hover:bg-[var(--color-neutral-50)] transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-inset",
        disabled && "opacity-50 cursor-not-allowed",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(
          "svg",
          {
            className: cn(
              "w-5 h-5 text-[var(--color-neutral-600)] transition-transform duration-200",
              isOpen && "transform rotate-180"
            ),
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24",
            children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" })
          }
        )
      ]
    }
  );
}
function AccordionContent({ children, className, ...props }) {
  const itemContext = useContext(AccordionItemContext);
  if (!itemContext) {
    throw new Error("AccordionContent must be used within AccordionItem");
  }
  const { isOpen } = itemContext;
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "px-4 py-3 text-sm text-[var(--color-neutral-700)] border-t border-[var(--color-neutral-200)]",
        "animate-in slide-in-from-top-2",
        className
      ),
      ...props,
      children
    }
  );
}
Accordion.displayName = "Accordion";
AccordionItem.displayName = "AccordionItem";
AccordionTrigger.displayName = "AccordionTrigger";
AccordionContent.displayName = "AccordionContent";
function Dropdown({ trigger, children, align = "left", open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isOpen = controlledOpen !== void 0 ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (controlledOpen === void 0) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);
  const alignmentClasses2 = {
    left: "left-0",
    right: "right-0",
    center: "left-1/2 -translate-x-1/2"
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative inline-block", ref: dropdownRef, children: [
    /* @__PURE__ */ jsx("div", { onClick: () => setOpen(!isOpen), children: trigger }),
    isOpen && /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "absolute top-full mt-2 z-50",
          "min-w-[200px] bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)]",
          "shadow-[var(--shadow-lg)] border border-[var(--color-neutral-200)]",
          "py-2",
          "animate-in fade-in slide-in-from-top-2",
          alignmentClasses2[align]
        ),
        children
      }
    )
  ] });
}
function DropdownItem({ children, disabled, icon, variant = "default", className, onClick, ...props }) {
  const variantClasses3 = {
    default: "text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]",
    danger: "text-[var(--color-brand-danger)] hover:bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)]"
  };
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      disabled,
      onClick,
      className: cn(
        "w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors",
        "focus:outline-none focus:bg-[var(--color-neutral-100)]",
        variantClasses3[variant],
        disabled && "opacity-50 cursor-not-allowed",
        className
      ),
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx("span", { className: "flex-1", children })
      ]
    }
  );
}
function DropdownSeparator({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("my-1 h-px bg-[var(--color-neutral-200)]", className),
      role: "separator",
      ...props
    }
  );
}
Dropdown.displayName = "Dropdown";
DropdownItem.displayName = "DropdownItem";
DropdownSeparator.displayName = "DropdownSeparator";
var sizeClasses = {
  left: {
    sm: "w-80",
    md: "w-96",
    lg: "w-[512px]",
    xl: "w-[640px]",
    full: "w-full"
  },
  right: {
    sm: "w-80",
    md: "w-96",
    lg: "w-[512px]",
    xl: "w-[640px]",
    full: "w-full"
  },
  top: {
    sm: "h-80",
    md: "h-96",
    lg: "h-[512px]",
    xl: "h-[640px]",
    full: "h-full"
  },
  bottom: {
    sm: "h-80",
    md: "h-96",
    lg: "h-[512px]",
    xl: "h-[640px]",
    full: "h-full"
  }
};
var positionClasses2 = {
  left: "top-0 left-0 h-full",
  right: "top-0 right-0 h-full",
  top: "top-0 left-0 w-full",
  bottom: "bottom-0 left-0 w-full"
};
var slideClasses = {
  left: "animate-in slide-in-from-left",
  right: "animate-in slide-in-from-right",
  top: "animate-in slide-in-from-top",
  bottom: "animate-in slide-in-from-bottom"
};
function Drawer({
  open,
  onClose,
  side = "right",
  title,
  description,
  children,
  footer,
  size = "md",
  className,
  ...props
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose]);
  if (!open) return null;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 bg-black/50 z-40 animate-in fade-in",
        onClick: onClose,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? "drawer-title" : void 0,
        "aria-describedby": description ? "drawer-description" : void 0,
        className: cn(
          "fixed z-50 bg-[var(--color-neutral-100)] shadow-[var(--shadow-xl)] flex flex-col",
          positionClasses2[side],
          sizeClasses[side][size],
          slideClasses[side],
          className
        ),
        ...props,
        children: [
          (title || description) && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 px-6 py-4 border-b border-[var(--color-neutral-200)]", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              title && /* @__PURE__ */ jsx("h2", { id: "drawer-title", className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: title }),
              description && /* @__PURE__ */ jsx("p", { id: "drawer-description", className: "mt-1 text-sm text-[var(--color-neutral-600)]", children: description })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: onClose,
                className: "flex-shrink-0 p-2 rounded-[var(--radius-md)] text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-100)] transition-colors",
                "aria-label": "Close drawer",
                children: /* @__PURE__ */ jsx(
                  "svg",
                  {
                    className: "w-5 h-5",
                    fill: "none",
                    stroke: "currentColor",
                    viewBox: "0 0 24 24",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        strokeWidth: 2,
                        d: "M6 18L18 6M6 6l12 12"
                      }
                    )
                  }
                )
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto px-6 py-4", children }),
          footer && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 px-6 py-4 border-t border-[var(--color-neutral-200)]", children: footer })
        ]
      }
    )
  ] });
}
Drawer.displayName = "Drawer";
var sizeClasses2 = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full mx-4"
};
function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  ...props
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && closeOnEscape) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose, closeOnEscape]);
  if (!open) return null;
  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose();
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 bg-black/50 z-40 animate-in fade-in",
        onClick: handleBackdropClick,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs(
      "div",
      {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? "dialog-title" : void 0,
        "aria-describedby": description ? "dialog-description" : void 0,
        className: cn(
          "w-full bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)]",
          "animate-in fade-in zoom-in-95",
          sizeClasses2[size],
          className
        ),
        onClick: (e) => e.stopPropagation(),
        ...props,
        children: [
          (title || description) && /* @__PURE__ */ jsx("div", { className: "px-6 py-4 border-b border-[var(--color-neutral-200)]", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              title && /* @__PURE__ */ jsx("h2", { id: "dialog-title", className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: title }),
              description && /* @__PURE__ */ jsx("p", { id: "dialog-description", className: "mt-1 text-sm text-[var(--color-neutral-600)]", children: description })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: onClose,
                className: "flex-shrink-0 p-2 rounded-[var(--radius-md)] text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-100)] transition-colors",
                "aria-label": "Close dialog",
                children: /* @__PURE__ */ jsx(
                  "svg",
                  {
                    className: "w-5 h-5",
                    fill: "none",
                    stroke: "currentColor",
                    viewBox: "0 0 24 24",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        strokeWidth: 2,
                        d: "M6 18L18 6M6 6l12 12"
                      }
                    )
                  }
                )
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx("div", { className: "px-6 py-4", children }),
          footer && /* @__PURE__ */ jsx("div", { className: "px-6 py-4 border-t border-[var(--color-neutral-200)] flex items-center justify-end gap-3", children: footer })
        ]
      }
    ) })
  ] });
}
Dialog.displayName = "Dialog";
var variantClasses = {
  default: "bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)] border-[var(--color-neutral-300)]",
  primary: "bg-[color-mix(in_srgb,var(--color-brand-primary)_12%,transparent)] text-[var(--color-brand-primary)] border-[var(--color-brand-primary)]",
  success: "bg-[color-mix(in_srgb,var(--color-semantic-success)_12%,transparent)] text-[var(--color-semantic-success)] border-[var(--color-semantic-success)]",
  warning: "bg-[color-mix(in_srgb,var(--color-brand-warning)_12%,transparent)] text-[var(--color-brand-warning)] border-[var(--color-brand-warning)]",
  danger: "bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)] text-[var(--color-brand-danger)] border-[var(--color-brand-danger)]",
  info: "bg-[color-mix(in_srgb,var(--color-semantic-info)_12%,transparent)] text-[var(--color-semantic-info)] border-[var(--color-semantic-info)]"
};
var sizeClasses3 = {
  sm: "px-2 py-0.5 text-xs",
  md: "px-3 py-1 text-sm",
  lg: "px-4 py-1.5 text-base"
};
function Tag({
  variant = "default",
  size = "md",
  children,
  icon,
  removable = false,
  onRemove,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1.5 font-medium rounded-full border",
        variantClasses[variant],
        sizeClasses3[size],
        className
      ),
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx("span", { children }),
        removable && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              onRemove?.();
            },
            className: "flex-shrink-0 p-0.5 rounded-full hover:bg-black/10 transition-colors",
            "aria-label": "Remove tag",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                className: "w-3 h-3",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M6 18L18 6M6 6l12 12"
                  }
                )
              }
            )
          }
        )
      ]
    }
  );
}
Tag.displayName = "Tag";
var spacingClasses = {
  horizontal: {
    none: "my-0",
    sm: "my-2",
    md: "my-4",
    lg: "my-8"
  },
  vertical: {
    none: "mx-0",
    sm: "mx-2",
    md: "mx-4",
    lg: "mx-8"
  }
};
function Divider({
  orientation = "horizontal",
  label,
  labelPosition = "center",
  spacing = "md",
  className,
  ...props
}) {
  if (orientation === "vertical") {
    return /* @__PURE__ */ jsx(
      "div",
      {
        role: "separator",
        "aria-orientation": "vertical",
        className: cn(
          "w-px bg-[var(--color-neutral-200)]",
          spacingClasses.vertical[spacing],
          className
        ),
        ...props
      }
    );
  }
  if (label) {
    const labelPositionClasses = {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end"
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        role: "separator",
        "aria-orientation": "horizontal",
        className: cn(
          "flex items-center",
          spacingClasses.horizontal[spacing],
          labelPositionClasses[labelPosition],
          className
        ),
        ...props,
        children: [
          labelPosition !== "left" && /* @__PURE__ */ jsx("div", { className: "flex-1 h-px bg-[var(--color-neutral-200)]" }),
          /* @__PURE__ */ jsx("span", { className: "px-3 text-sm text-[var(--color-neutral-600)] font-medium", children: label }),
          labelPosition !== "right" && /* @__PURE__ */ jsx("div", { className: "flex-1 h-px bg-[var(--color-neutral-200)]" })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "separator",
      "aria-orientation": "horizontal",
      className: cn(
        "h-px bg-[var(--color-neutral-200)]",
        spacingClasses.horizontal[spacing],
        className
      ),
      ...props
    }
  );
}
Divider.displayName = "Divider";
var sizeClasses4 = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg"
};
var spacingClasses2 = {
  overlap: "-space-x-3",
  touching: "space-x-0",
  normal: "space-x-2"
};
function AvatarGroup({
  children,
  max = 5,
  size = "md",
  spacing = "overlap",
  className,
  ...props
}) {
  const childrenArray = Array.isArray(children) ? children : [children];
  const visibleChildren = childrenArray.slice(0, max);
  const remaining = childrenArray.length - max;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("flex items-center", spacingClasses2[spacing], className),
      ...props,
      children: [
        visibleChildren.map((child, index) => /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "relative rounded-full ring-2 ring-white",
              spacing === "overlap" && "hover:z-10 transition-all"
            ),
            children: child
          },
          index
        )),
        remaining > 0 && /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "relative flex items-center justify-center rounded-full",
              "bg-[var(--color-neutral-200)] text-[var(--color-neutral-700)] font-medium",
              "ring-2 ring-white",
              sizeClasses4[size]
            ),
            children: [
              "+",
              remaining
            ]
          }
        )
      ]
    }
  );
}
AvatarGroup.displayName = "AvatarGroup";
var variantClasses2 = {
  primary: "bg-[var(--color-brand-primary)] text-white hover:opacity-90",
  secondary: "bg-[var(--color-brand-secondary)] text-white hover:opacity-90",
  ghost: "bg-transparent text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]",
  danger: "bg-[var(--color-brand-danger)] text-white hover:opacity-90"
};
var sizeClasses5 = {
  sm: "h-8 w-8 text-sm",
  md: "h-10 w-10 text-base",
  lg: "h-12 w-12 text-lg",
  xl: "h-14 w-14 text-xl"
};
var shapeClasses = {
  square: "rounded-none",
  rounded: "rounded-[var(--radius-md)]",
  circle: "rounded-full"
};
var IconButton = forwardRef(
  ({
    icon,
    "aria-label": ariaLabel,
    variant = "ghost",
    size = "md",
    shape = "rounded",
    disabled = false,
    loading = false,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type: "button",
        "aria-label": ariaLabel,
        disabled: disabled || loading,
        className: cn(
          // Base styles
          "inline-flex items-center justify-center",
          "transition-all duration-[var(--transition-duration-base)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:ring-offset-2",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Variant
          variantClasses2[variant],
          // Size
          sizeClasses5[size],
          // Shape
          shapeClasses[shape],
          // Custom className
          className
        ),
        ...props,
        children: loading ? /* @__PURE__ */ jsxs(
          "svg",
          {
            className: "animate-spin h-5 w-5",
            xmlns: "http://www.w3.org/2000/svg",
            fill: "none",
            viewBox: "0 0 24 24",
            children: [
              /* @__PURE__ */ jsx(
                "circle",
                {
                  className: "opacity-25",
                  cx: "12",
                  cy: "12",
                  r: "10",
                  stroke: "currentColor",
                  strokeWidth: "4"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  className: "opacity-75",
                  fill: "currentColor",
                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                }
              )
            ]
          }
        ) : icon
      }
    );
  }
);
IconButton.displayName = "IconButton";
var spacingClasses3 = {
  horizontal: {
    "0": "gap-0",
    "1": "gap-1",
    "2": "gap-2",
    "3": "gap-3",
    "4": "gap-4"
  },
  vertical: {
    "0": "gap-0",
    "1": "gap-1",
    "2": "gap-2",
    "3": "gap-3",
    "4": "gap-4"
  }
};
function ButtonGroup({
  children,
  orientation = "horizontal",
  attached = true,
  spacing = "2",
  className,
  ...props
}) {
  if (attached) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        role: "group",
        className: cn(
          "inline-flex",
          orientation === "horizontal" ? "flex-row" : "flex-col",
          // Remove rounded corners from middle buttons
          "[&>button:not(:first-child):not(:last-child)]:rounded-none",
          // Round only appropriate corners for first and last buttons
          orientation === "horizontal" ? "[&>button:first-child]:rounded-r-none [&>button:last-child]:rounded-l-none" : "[&>button:first-child]:rounded-b-none [&>button:last-child]:rounded-t-none",
          // Remove borders between buttons
          orientation === "horizontal" ? "[&>button:not(:last-child)]:border-r-0" : "[&>button:not(:last-child)]:border-b-0",
          className
        ),
        ...props,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "group",
      className: cn(
        "inline-flex",
        orientation === "horizontal" ? "flex-row" : "flex-col",
        spacingClasses3[orientation][spacing],
        className
      ),
      ...props,
      children
    }
  );
}
ButtonGroup.displayName = "ButtonGroup";
function Menu({
  items,
  trigger,
  onSelect,
  open: controlledOpen,
  onOpenChange,
  align = "left",
  className,
  ...props
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState(/* @__PURE__ */ new Set());
  const menuRef = useRef(null);
  const isOpen = controlledOpen !== void 0 ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (controlledOpen === void 0) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
        setOpenSubmenus(/* @__PURE__ */ new Set());
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);
  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.submenu) {
      setOpenSubmenus((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.add(item.id);
        }
        return next;
      });
    } else {
      item.onClick?.();
      onSelect?.(item);
      setOpen(false);
      setOpenSubmenus(/* @__PURE__ */ new Set());
    }
  };
  const renderMenuItem = (item, level = 0) => {
    if (item.divider) {
      return /* @__PURE__ */ jsx(
        "div",
        {
          className: "my-1 h-px bg-[var(--color-neutral-200)]",
          role: "separator"
        },
        item.id
      );
    }
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuOpen = openSubmenus.has(item.id);
    return /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          disabled: item.disabled,
          onClick: () => handleItemClick(item),
          className: cn(
            "w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors",
            "focus:outline-none focus:bg-[var(--color-neutral-100)]",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            item.disabled ? "text-[var(--color-neutral-400)]" : "text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]"
          ),
          style: { paddingLeft: `${16 + level * 12}px` },
          children: [
            item.icon && /* @__PURE__ */ jsx("span", { className: "flex-shrink-0", children: item.icon }),
            /* @__PURE__ */ jsx("span", { className: "flex-1", children: item.label }),
            hasSubmenu && /* @__PURE__ */ jsx(
              "svg",
              {
                className: cn(
                  "w-4 h-4 flex-shrink-0 transition-transform",
                  isSubmenuOpen && "rotate-90"
                ),
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeWidth: 2,
                    d: "M9 5l7 7-7 7"
                  }
                )
              }
            )
          ]
        }
      ),
      hasSubmenu && isSubmenuOpen && /* @__PURE__ */ jsx("div", { className: "py-1", children: item.submenu.map((subitem) => renderMenuItem(subitem, level + 1)) })
    ] }, item.id);
  };
  const alignmentClasses2 = {
    left: "left-0",
    right: "right-0"
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative inline-block", ref: menuRef, children: [
    trigger && /* @__PURE__ */ jsx("div", { onClick: () => setOpen(!isOpen), children: trigger }),
    isOpen && /* @__PURE__ */ jsx(
      "div",
      {
        role: "menu",
        className: cn(
          "absolute top-full mt-2 z-50",
          "min-w-[200px] bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)]",
          "shadow-[var(--shadow-lg)] border border-[var(--color-neutral-200)]",
          "py-2",
          "animate-in fade-in slide-in-from-top-2",
          alignmentClasses2[align],
          className
        ),
        ...props,
        children: items.map((item) => renderMenuItem(item))
      }
    )
  ] });
}
Menu.displayName = "Menu";
var positionClasses3 = {
  top: "bottom-full mb-2",
  bottom: "top-full mt-2",
  left: "right-full mr-2",
  right: "left-full ml-2"
};
var alignClasses = {
  top: {
    start: "left-0",
    center: "left-1/2 -translate-x-1/2",
    end: "right-0"
  },
  bottom: {
    start: "left-0",
    center: "left-1/2 -translate-x-1/2",
    end: "right-0"
  },
  left: {
    start: "top-0",
    center: "top-1/2 -translate-y-1/2",
    end: "bottom-0"
  },
  right: {
    start: "top-0",
    center: "top-1/2 -translate-y-1/2",
    end: "bottom-0"
  }
};
function Popover({
  trigger: triggerProp,
  children,
  title,
  position = "bottom",
  align = "start",
  open: controlledOpen,
  onOpenChange,
  triggerType = "click",
  delay = 200
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const popoverRef = useRef(null);
  const isOpen = controlledOpen !== void 0 ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (controlledOpen === void 0) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (isOpen && triggerType === "click") {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, triggerType]);
  const handleMouseEnter = () => {
    if (triggerType === "hover") {
      const id = setTimeout(() => {
        setOpen(true);
      }, delay);
      setTimeoutId(id);
    }
  };
  const handleMouseLeave = () => {
    if (triggerType === "hover") {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setOpen(false);
    }
  };
  const handleClick = () => {
    if (triggerType === "click") {
      setOpen(!isOpen);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "relative inline-block",
      ref: popoverRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [
        /* @__PURE__ */ jsx("div", { onClick: handleClick, children: triggerProp }),
        isOpen && /* @__PURE__ */ jsxs(
          "div",
          {
            role: "dialog",
            "aria-modal": "false",
            className: cn(
              "absolute z-50 w-80 bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)]",
              "shadow-[var(--shadow-lg)] border border-[var(--color-neutral-200)]",
              "animate-in fade-in zoom-in-95",
              positionClasses3[position],
              alignClasses[position][align]
            ),
            children: [
              title && /* @__PURE__ */ jsx("div", { className: "px-4 py-3 border-b border-[var(--color-neutral-200)]", children: /* @__PURE__ */ jsx("h3", { className: "font-semibold text-[var(--color-neutral-900)]", children: title }) }),
              /* @__PURE__ */ jsx("div", { className: "px-4 py-3 text-sm text-[var(--color-neutral-700)]", children }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: cn(
                    "absolute w-3 h-3 bg-[var(--color-neutral-100)] border transform rotate-45",
                    position === "top" && "top-full -mt-1.5 border-b border-r border-t-0 border-l-0",
                    position === "bottom" && "bottom-full -mb-1.5 border-t border-l border-b-0 border-r-0",
                    position === "left" && "left-full -ml-1.5 border-r border-b border-t-0 border-l-0",
                    position === "right" && "right-full -mr-1.5 border-l border-t border-b-0 border-r-0",
                    align === "start" && (position === "top" || position === "bottom") && "left-6",
                    align === "center" && (position === "top" || position === "bottom") && "left-1/2 -translate-x-1/2",
                    align === "end" && (position === "top" || position === "bottom") && "right-6",
                    align === "start" && (position === "left" || position === "right") && "top-6",
                    align === "center" && (position === "left" || position === "right") && "top-1/2 -translate-y-1/2",
                    align === "end" && (position === "left" || position === "right") && "bottom-6"
                  )
                }
              )
            ]
          }
        )
      ]
    }
  );
}
Popover.displayName = "Popover";
var statusVariants = {
  active: "success",
  inactive: "default",
  error: "danger"
};
var AgentCard = forwardRef(
  ({ agent, actions, clickable = false, className, onClick, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] border border-[var(--color-neutral-200)]",
          "shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)]",
          "transition-all duration-[var(--transition-duration-base)]",
          "p-6",
          clickable && "cursor-pointer hover:border-[var(--color-brand-primary)]",
          className
        ),
        onClick: clickable ? onClick : void 0,
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4 mb-4", children: [
            /* @__PURE__ */ jsx(
              Avatar,
              {
                src: agent.avatar,
                fallback: agent.name.charAt(0),
                size: "lg",
                backgroundColor: agent.displayColor
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-1", children: [
                /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-[var(--color-neutral-900)] truncate", children: agent.name }),
                /* @__PURE__ */ jsx(Badge, { variant: statusVariants[agent.status], size: "sm", dot: true, children: agent.status })
              ] }),
              /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] line-clamp-2", children: agent.description })
            ] })
          ] }),
          agent.tags && agent.tags.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mb-4", children: agent.tags.map((tag) => /* @__PURE__ */ jsx(Badge, { variant: "default", size: "sm", children: tag }, tag)) }),
          agent.metrics && /* @__PURE__ */ jsxs("div", { className: "flex gap-4 mb-4 p-3 bg-[var(--color-neutral-50)] rounded-[var(--radius-md)]", children: [
            agent.metrics.tasksCompleted !== void 0 && /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              /* @__PURE__ */ jsx("div", { className: "text-xs text-[var(--color-neutral-600)]", children: "Tasks" }),
              /* @__PURE__ */ jsx("div", { className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: agent.metrics.tasksCompleted })
            ] }),
            agent.metrics.successRate !== void 0 && /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              /* @__PURE__ */ jsx("div", { className: "text-xs text-[var(--color-neutral-600)]", children: "Success Rate" }),
              /* @__PURE__ */ jsxs("div", { className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: [
                agent.metrics.successRate,
                "%"
              ] })
            ] }),
            agent.metrics.lastActive && /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              /* @__PURE__ */ jsx("div", { className: "text-xs text-[var(--color-neutral-600)]", children: "Last Active" }),
              /* @__PURE__ */ jsx("div", { className: "text-sm font-medium text-[var(--color-neutral-900)]", children: agent.metrics.lastActive })
            ] })
          ] }),
          actions && actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex gap-2 pt-4 border-t border-[var(--color-neutral-200)]", children: actions.map((action, index) => /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: (e) => {
                e.stopPropagation();
                action.onClick();
              },
              className: cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-md)]",
                "text-sm font-medium transition-colors duration-[var(--transition-duration-base)]",
                "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)]",
                action.variant === "danger" ? "text-[var(--color-brand-danger)] hover:bg-[var(--color-brand-danger)]/10" : "text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)]"
              ),
              children: [
                action.icon,
                action.label
              ]
            },
            index
          )) })
        ]
      }
    );
  }
);
AgentCard.displayName = "AgentCard";
var ChatBubble = forwardRef(
  ({
    message,
    showTimestamp = true,
    showAvatar = true,
    className,
    ...props
  }, ref) => {
    const isUser = message.sender === "user";
    const isSystem = message.sender === "system";
    const formatTime = (date) => {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit"
      });
    };
    if (isSystem) {
      return /* @__PURE__ */ jsx(
        "div",
        {
          ref,
          className: cn(
            "flex justify-center my-4",
            className
          ),
          ...props,
          children: /* @__PURE__ */ jsx("div", { className: "px-3 py-1 bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)] text-xs rounded-full", children: message.content })
        }
      );
    }
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "flex gap-3 mb-4",
          isUser && "flex-row-reverse",
          className
        ),
        ...props,
        children: [
          showAvatar && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx(
            Avatar,
            {
              src: isUser ? void 0 : message.agentAvatar,
              fallback: isUser ? "U" : message.agentName?.charAt(0) || "A",
              size: "sm",
              backgroundColor: message.agentColor
            }
          ) }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex flex-col gap-1 max-w-[70%]",
                isUser && "items-end"
              ),
              children: [
                !isUser && message.agentName && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-600)] px-3", children: message.agentName }),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "px-4 py-2 rounded-2xl",
                      isUser ? "bg-[var(--color-brand-primary)] text-white rounded-tr-sm" : "bg-[var(--color-neutral-100)] text-[var(--color-neutral-900)] rounded-tl-sm"
                    ),
                    children: /* @__PURE__ */ jsx("p", { className: "text-sm whitespace-pre-wrap break-words", children: message.content })
                  }
                ),
                showTimestamp && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-500)] px-3", children: formatTime(message.timestamp) })
              ]
            }
          )
        ]
      }
    );
  }
);
ChatBubble.displayName = "ChatBubble";
var statusConfig = {
  active: {
    color: "bg-[var(--color-semantic-success)]",
    label: "Active",
    ringColor: "ring-[var(--color-semantic-success)]/20"
  },
  inactive: {
    color: "bg-[var(--color-neutral-400)]",
    label: "Inactive",
    ringColor: "ring-[var(--color-neutral-400)]/20"
  },
  error: {
    color: "bg-[var(--color-brand-danger)]",
    label: "Error",
    ringColor: "ring-[var(--color-brand-danger)]/20"
  },
  processing: {
    color: "bg-[var(--color-brand-primary)]",
    label: "Processing",
    ringColor: "ring-[var(--color-brand-primary)]/20"
  },
  idle: {
    color: "bg-[var(--color-brand-warning)]",
    label: "Idle",
    ringColor: "ring-[var(--color-brand-warning)]/20"
  }
};
var dotSizes = {
  sm: "w-2 h-2",
  md: "w-3 h-3",
  lg: "w-4 h-4"
};
var textSizes = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base"
};
var AgentStatus = forwardRef(
  ({
    status,
    size = "md",
    showLabel = true,
    label,
    className,
    ...props
  }, ref) => {
    const config = statusConfig[status];
    const displayLabel = label || config.label;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "inline-flex items-center gap-2",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("span", { className: "relative flex", children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "inline-flex rounded-full",
                  config.color,
                  dotSizes[size],
                  status === "processing" && "animate-pulse"
                )
              }
            ),
            status === "active" && /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "absolute inline-flex rounded-full opacity-75",
                  config.color,
                  dotSizes[size],
                  "animate-ping"
                )
              }
            )
          ] }),
          showLabel && /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "font-medium text-[var(--color-neutral-700)]",
                textSizes[size]
              ),
              children: displayLabel
            }
          )
        ]
      }
    );
  }
);
AgentStatus.displayName = "AgentStatus";
var statusColors2 = {
  active: "bg-[var(--color-semantic-success)]",
  inactive: "bg-[var(--color-neutral-400)]",
  processing: "bg-[var(--color-semantic-info)]",
  error: "bg-[var(--color-brand-danger)]"
};
var statusLabels = {
  active: "Active",
  inactive: "Inactive",
  processing: "Processing",
  error: "Error"
};
function AgentList({
  agents,
  variant = "list",
  showMetrics = true,
  onAgentClick,
  renderActions,
  emptyState,
  className,
  ...props
}) {
  if (agents.length === 0 && emptyState) {
    return /* @__PURE__ */ jsx("div", { className: cn("py-12", className), children: emptyState });
  }
  if (variant === "grid") {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className),
        ...props,
        children: agents.map((agent) => /* @__PURE__ */ jsxs(
          "div",
          {
            onClick: () => onAgentClick?.(agent),
            className: cn(
              "p-4 rounded-[var(--radius-lg)] border border-[var(--color-neutral-200)]",
              "bg-[var(--color-neutral-100)] hover:shadow-[var(--shadow-md)] transition-shadow",
              onAgentClick && "cursor-pointer"
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 mb-3", children: [
                agent.avatar ? /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: agent.avatar,
                    alt: agent.name,
                    className: "w-12 h-12 rounded-full"
                  }
                ) : /* @__PURE__ */ jsx("div", { className: "w-12 h-12 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center font-semibold", children: agent.name.charAt(0).toUpperCase() }),
                /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ jsx("h3", { className: "font-semibold text-[var(--color-neutral-900)] truncate", children: agent.name }),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-1", children: [
                    /* @__PURE__ */ jsx("span", { className: cn("w-2 h-2 rounded-full", statusColors2[agent.status]) }),
                    /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-600)]", children: statusLabels[agent.status] })
                  ] })
                ] })
              ] }),
              agent.description && /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] mb-3 line-clamp-2", children: agent.description }),
              showMetrics && agent.metrics && /* @__PURE__ */ jsxs("div", { className: "flex gap-4 text-xs text-[var(--color-neutral-600)] mb-3", children: [
                agent.metrics.tasks !== void 0 && /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx("span", { className: "font-medium", children: agent.metrics.tasks }),
                  " tasks"
                ] }),
                agent.metrics.uptime && /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx("span", { className: "font-medium", children: agent.metrics.uptime }),
                  " uptime"
                ] })
              ] }),
              renderActions && /* @__PURE__ */ jsx("div", { className: "mt-3", children: renderActions(agent) })
            ]
          },
          agent.id
        ))
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), ...props, children: agents.map((agent) => /* @__PURE__ */ jsxs(
    "div",
    {
      onClick: () => onAgentClick?.(agent),
      className: cn(
        "flex items-center gap-4 p-4 rounded-[var(--radius-lg)]",
        "border border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)]",
        "hover:shadow-[var(--shadow-sm)] transition-shadow",
        onAgentClick && "cursor-pointer"
      ),
      children: [
        agent.avatar ? /* @__PURE__ */ jsx(
          "img",
          {
            src: agent.avatar,
            alt: agent.name,
            className: "w-12 h-12 rounded-full flex-shrink-0"
          }
        ) : /* @__PURE__ */ jsx("div", { className: "w-12 h-12 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center font-semibold flex-shrink-0", children: agent.name.charAt(0).toUpperCase() }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-1", children: [
            /* @__PURE__ */ jsx("h3", { className: "font-semibold text-[var(--color-neutral-900)] truncate", children: agent.name }),
            /* @__PURE__ */ jsx("span", { className: cn("w-2 h-2 rounded-full flex-shrink-0", statusColors2[agent.status]) }),
            /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-600)] flex-shrink-0", children: statusLabels[agent.status] })
          ] }),
          agent.description && /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] truncate", children: agent.description }),
          showMetrics && agent.metrics && /* @__PURE__ */ jsxs("div", { className: "flex gap-4 text-xs text-[var(--color-neutral-600)] mt-1", children: [
            agent.metrics.tasks !== void 0 && /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium", children: agent.metrics.tasks }),
              " tasks"
            ] }),
            agent.metrics.lastActive && /* @__PURE__ */ jsxs("div", { children: [
              "Last active: ",
              agent.metrics.lastActive
            ] })
          ] })
        ] }),
        renderActions && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", onClick: (e) => e.stopPropagation(), children: renderActions(agent) })
      ]
    },
    agent.id
  )) });
}
AgentList.displayName = "AgentList";
function ChatInput({
  placeholder = "Type a message...",
  disabled = false,
  allowFiles = false,
  allowEmoji = false,
  maxLength,
  onSubmit,
  onFilesSelect,
  autoFocus = false,
  className,
  ...props
}) {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const handleSubmit = () => {
    if (message.trim() || files.length > 0) {
      onSubmit(message.trim(), files);
      setMessage("");
      setFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
    onFilesSelect?.(selectedFiles);
  };
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const handleInput = (e) => {
    const target = e.currentTarget;
    setMessage(target.value);
    target.style.height = "auto";
    target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
  };
  const canSubmit = (message.trim().length > 0 || files.length > 0) && !disabled;
  return /* @__PURE__ */ jsxs("div", { className: cn("bg-[var(--color-neutral-100)] border border-[var(--color-neutral-200)] rounded-[var(--radius-lg)] p-3", className), ...props, children: [
    files.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mb-2", children: files.map((file, index) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-md)] bg-[var(--color-neutral-100)] text-sm",
        children: [
          /* @__PURE__ */ jsx("svg", { className: "w-4 h-4 text-[var(--color-neutral-600)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) }),
          /* @__PURE__ */ jsx("span", { className: "text-[var(--color-neutral-700)] truncate max-w-[150px]", children: file.name }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => removeFile(index),
              className: "text-[var(--color-neutral-600)] hover:text-[var(--color-neutral-900)]",
              children: /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) })
            }
          )
        ]
      },
      index
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex gap-1 flex-shrink-0", children: [
        allowFiles && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: fileInputRef,
              type: "file",
              multiple: true,
              className: "hidden",
              onChange: handleFileChange,
              disabled
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => fileInputRef.current?.click(),
              disabled,
              className: "p-2 rounded-[var(--radius-md)] text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-100)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
              "aria-label": "Attach file",
              children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) })
            }
          )
        ] }),
        allowEmoji && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            disabled,
            className: "p-2 rounded-[var(--radius-md)] text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-100)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
            "aria-label": "Add emoji",
            children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) })
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          ref: textareaRef,
          value: message,
          onInput: handleInput,
          onKeyDown: handleKeyDown,
          placeholder,
          disabled,
          maxLength,
          autoFocus,
          rows: 1,
          className: cn(
            "flex-1 resize-none bg-transparent",
            "text-sm text-[var(--color-neutral-900)] placeholder:text-[var(--color-neutral-400)]",
            "focus:outline-none",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "max-h-[200px]"
          )
        }
      ),
      maxLength && /* @__PURE__ */ jsxs("span", { className: "text-xs text-[var(--color-neutral-500)] flex-shrink-0", children: [
        message.length,
        "/",
        maxLength
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: handleSubmit,
          disabled: !canSubmit,
          className: cn(
            "p-2 rounded-[var(--radius-md)] transition-colors flex-shrink-0",
            canSubmit ? "bg-[var(--color-brand-primary)] text-white hover:opacity-90" : "bg-[var(--color-neutral-100)] text-[var(--color-neutral-400)] cursor-not-allowed"
          ),
          "aria-label": "Send message",
          children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8" }) })
        }
      )
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-neutral-500)] mt-2", children: "Press Enter to send, Shift + Enter for new line" })
  ] });
}
ChatInput.displayName = "ChatInput";
function ChatThread({
  messages,
  currentUserId,
  showAvatars = true,
  showTimestamps = true,
  autoScroll = true,
  loading = false,
  emptyState,
  className,
  ...props
}) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);
  if (messages.length === 0 && !loading) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center h-full", className), ...props, children: emptyState || /* @__PURE__ */ jsxs("div", { className: "text-center text-[var(--color-neutral-600)]", children: [
      /* @__PURE__ */ jsx("p", { children: "No messages yet" }),
      /* @__PURE__ */ jsx("p", { className: "text-sm mt-1", children: "Start a conversation!" })
    ] }) });
  }
  const formatTimestamp = (timestamp) => {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: scrollRef,
      className: cn("overflow-y-auto space-y-4 p-4", className),
      ...props,
      children: [
        messages.map((message) => {
          const isCurrentUser = message.sender.id === currentUserId;
          const isAgent = message.sender.type === "agent";
          const isSystem = message.sender.type === "system";
          const showAvatar = showAvatars && !isSystem;
          if (isSystem) {
            return /* @__PURE__ */ jsx(
              "div",
              {
                className: "flex justify-center",
                children: /* @__PURE__ */ jsx("div", { className: "px-4 py-2 rounded-[var(--radius-lg)] bg-[var(--color-neutral-100)] text-xs text-[var(--color-neutral-600)] max-w-md text-center", children: message.content })
              },
              message.id
            );
          }
          return /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex gap-3",
                isCurrentUser ? "flex-row-reverse" : "flex-row"
              ),
              children: [
                showAvatar && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: message.sender.avatar ? /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: message.sender.avatar,
                    alt: message.sender.name,
                    className: "w-8 h-8 rounded-full"
                  }
                ) : /* @__PURE__ */ jsx("div", { className: cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold",
                  isAgent ? "bg-[var(--color-brand-primary)]" : "bg-[var(--color-brand-secondary)]"
                ), children: message.sender.name.charAt(0).toUpperCase() }) }),
                /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", isCurrentUser ? "items-end" : "items-start", "max-w-[70%]"), children: [
                  !isCurrentUser && /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-[var(--color-neutral-600)] px-3", children: message.sender.name }),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: cn(
                        "px-4 py-2 rounded-[var(--radius-lg)]",
                        isCurrentUser ? "bg-[var(--color-brand-primary)] text-white" : isAgent ? "bg-[var(--color-neutral-100)] text-[var(--color-neutral-900)]" : "bg-[var(--color-neutral-100)] text-[var(--color-neutral-900)]",
                        message.status === "error" && "border-2 border-[var(--color-brand-danger)]"
                      ),
                      children: [
                        /* @__PURE__ */ jsx("p", { className: "text-sm whitespace-pre-wrap break-words", children: message.content }),
                        message.attachments && message.attachments.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-2 space-y-1", children: message.attachments.map((attachment, i) => /* @__PURE__ */ jsxs(
                          "a",
                          {
                            href: attachment.url,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            className: cn(
                              "flex items-center gap-2 px-2 py-1 rounded text-xs",
                              isCurrentUser ? "bg-white/20" : "bg-[var(--color-neutral-200)]"
                            ),
                            children: [
                              /* @__PURE__ */ jsx("svg", { className: "w-3 h-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) }),
                              /* @__PURE__ */ jsx("span", { className: "truncate", children: attachment.name })
                            ]
                          },
                          i
                        )) })
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-3", children: [
                    showTimestamps && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-500)]", children: formatTimestamp(message.timestamp) }),
                    message.status === "sending" && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-500)]", children: "Sending..." }),
                    message.status === "error" && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-brand-danger)]", children: "Failed to send" })
                  ] })
                ] })
              ]
            },
            message.id
          );
        }),
        loading && /* @__PURE__ */ jsxs("div", { className: "flex gap-3", children: [
          /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0", children: "AI" }),
          /* @__PURE__ */ jsx("div", { className: "px-4 py-3 rounded-[var(--radius-lg)] bg-[var(--color-neutral-100)]", children: /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
            /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-[var(--color-neutral-400)] animate-bounce", style: { animationDelay: "0ms" } }),
            /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-[var(--color-neutral-400)] animate-bounce", style: { animationDelay: "150ms" } }),
            /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-[var(--color-neutral-400)] animate-bounce", style: { animationDelay: "300ms" } })
          ] }) })
        ] })
      ]
    }
  );
}
ChatThread.displayName = "ChatThread";
var statusColors3 = {
  pending: {
    bg: "bg-[var(--color-neutral-100)]",
    text: "text-[var(--color-neutral-600)]",
    border: "border-[var(--color-neutral-300)]"
  },
  active: {
    bg: "bg-[color-mix(in_srgb,var(--color-semantic-info)_12%,transparent)]",
    text: "text-[var(--color-semantic-info)]",
    border: "border-[var(--color-semantic-info)]"
  },
  completed: {
    bg: "bg-[color-mix(in_srgb,var(--color-semantic-success)_12%,transparent)]",
    text: "text-[var(--color-semantic-success)]",
    border: "border-[var(--color-semantic-success)]"
  },
  error: {
    bg: "bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)]",
    text: "text-[var(--color-brand-danger)]",
    border: "border-[var(--color-brand-danger)]"
  },
  skipped: {
    bg: "bg-[var(--color-neutral-100)]",
    text: "text-[var(--color-neutral-500)]",
    border: "border-[var(--color-neutral-300)]"
  }
};
var workflowStatusColors = {
  idle: "text-[var(--color-neutral-600)]",
  running: "text-[var(--color-semantic-info)]",
  completed: "text-[var(--color-semantic-success)]",
  error: "text-[var(--color-brand-danger)]",
  paused: "text-[var(--color-brand-warning)]"
};
function WorkflowCard({
  title,
  description,
  steps,
  status,
  progress,
  actions,
  onStepClick,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "bg-[var(--color-neutral-100)] border border-[var(--color-neutral-200)] rounded-[var(--radius-lg)] overflow-hidden",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "px-4 py-3 border-b border-[var(--color-neutral-200)] bg-[var(--color-neutral-50)]", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx("h3", { className: "font-semibold text-[var(--color-neutral-900)] truncate", children: title }),
              description && /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] mt-0.5", children: description }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-2", children: [
                /* @__PURE__ */ jsx("span", { className: cn("text-xs font-medium", workflowStatusColors[status]), children: status.charAt(0).toUpperCase() + status.slice(1) }),
                progress !== void 0 && /* @__PURE__ */ jsxs("span", { className: "text-xs text-[var(--color-neutral-600)]", children: [
                  progress,
                  "% Complete"
                ] })
              ] })
            ] }),
            actions && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: actions })
          ] }),
          progress !== void 0 && /* @__PURE__ */ jsx("div", { className: "mt-3 h-1.5 bg-[var(--color-neutral-200)] rounded-full overflow-hidden", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "h-full transition-all duration-300",
                status === "error" ? "bg-[var(--color-brand-danger)]" : status === "completed" ? "bg-[var(--color-semantic-success)]" : "bg-[var(--color-brand-primary)]"
              ),
              style: { width: `${progress}%` }
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "p-4", children: /* @__PURE__ */ jsx("div", { className: "space-y-3", children: steps.map((step, index) => {
          const colors = statusColors3[step.status];
          const isLast = index === steps.length - 1;
          return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                onClick: () => onStepClick?.(step),
                className: cn(
                  "flex items-start gap-3 p-3 rounded-[var(--radius-md)]",
                  "border-l-4",
                  colors.border,
                  colors.bg,
                  onStepClick && "cursor-pointer hover:opacity-80 transition-opacity"
                ),
                children: [
                  /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 pt-0.5", children: step.icon ? /* @__PURE__ */ jsx("div", { className: cn("w-5 h-5", colors.text), children: step.icon }) : /* @__PURE__ */ jsxs("div", { className: cn("w-5 h-5 rounded-full border-2", colors.border, colors.bg, "flex items-center justify-center"), children: [
                    step.status === "completed" && /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 text-[var(--color-semantic-success)]", fill: "currentColor", viewBox: "0 0 20 20", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) }),
                    step.status === "error" && /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 text-[var(--color-brand-danger)]", fill: "currentColor", viewBox: "0 0 20 20", children: /* @__PURE__ */ jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) }),
                    step.status === "active" && /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-[var(--color-semantic-info)] animate-pulse" })
                  ] }) }),
                  /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
                      /* @__PURE__ */ jsx("h4", { className: cn("font-medium text-sm", colors.text), children: step.title }),
                      step.duration && /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--color-neutral-500)] flex-shrink-0", children: step.duration })
                    ] }),
                    step.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-neutral-600)] mt-1", children: step.description }),
                    step.error && /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-brand-danger)] mt-1", children: step.error })
                  ] })
                ]
              }
            ),
            !isLast && /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "absolute left-6 top-[calc(100%)] w-px h-3 -translate-x-1/2",
                  steps[index + 1].status === "completed" ? "bg-[var(--color-semantic-success)]" : "bg-[var(--color-neutral-300)]"
                )
              }
            )
          ] }, step.id);
        }) }) })
      ]
    }
  );
}
WorkflowCard.displayName = "WorkflowCard";
var StatsCard = forwardRef(
  ({
    label,
    value,
    change,
    trend = "neutral",
    icon,
    description,
    className,
    ...props
  }, ref) => {
    const trendColor = {
      positive: "text-[var(--color-semantic-success)]",
      negative: "text-[var(--color-brand-danger)]",
      neutral: "text-[var(--color-neutral-600)]"
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] border border-[var(--color-neutral-200)]",
          "shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)]",
          "transition-shadow duration-[var(--transition-duration-base)]",
          "p-6",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between mb-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
              /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-[var(--color-neutral-600)] mb-1", children: label }),
              /* @__PURE__ */ jsx("p", { className: "text-3xl font-bold text-[var(--color-neutral-900)]", children: value })
            ] }),
            icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 p-3 bg-[var(--color-brand-primary)]/10 rounded-[var(--radius-md)] text-[var(--color-brand-primary)]", children: icon })
          ] }),
          change && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
            /* @__PURE__ */ jsx("span", { className: cn("text-sm font-medium", trendColor[trend]), children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center", children: [
              change.direction === "up" ? /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 10l7-7m0 0l7 7m-7-7v18" }) }) : /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 14l-7 7m0 0l-7-7m7 7V3" }) }),
              change.value,
              "%"
            ] }) }),
            /* @__PURE__ */ jsx("span", { className: "text-sm text-[var(--color-neutral-600)]", children: "vs last period" })
          ] }),
          description && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-[var(--color-neutral-600)]", children: description })
        ]
      }
    );
  }
);
StatsCard.displayName = "StatsCard";
var variantColors = {
  default: {
    bg: "bg-[var(--color-neutral-100)]",
    border: "border-[var(--color-neutral-300)]",
    line: "bg-[var(--color-neutral-300)]"
  },
  success: {
    bg: "bg-[color-mix(in_srgb,var(--color-semantic-success)_12%,transparent)]",
    border: "border-[var(--color-semantic-success)]",
    line: "bg-[var(--color-semantic-success)]"
  },
  warning: {
    bg: "bg-[color-mix(in_srgb,var(--color-brand-warning)_12%,transparent)]",
    border: "border-[var(--color-brand-warning)]",
    line: "bg-[var(--color-brand-warning)]"
  },
  danger: {
    bg: "bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)]",
    border: "border-[var(--color-brand-danger)]",
    line: "bg-[var(--color-brand-danger)]"
  },
  info: {
    bg: "bg-[color-mix(in_srgb,var(--color-semantic-info)_12%,transparent)]",
    border: "border-[var(--color-semantic-info)]",
    line: "bg-[var(--color-semantic-info)]"
  }
};
function Timeline({
  items,
  orientation = "vertical",
  linePosition = "left",
  onItemClick,
  className,
  ...props
}) {
  const formatTimestamp = (timestamp) => {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleString();
  };
  if (orientation === "horizontal") {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: cn("flex items-start gap-4 overflow-x-auto pb-4", className),
        ...props,
        children: items.map((item, index) => {
          const colors = variantColors[item.variant || "default"];
          const isLast = index === items.length - 1;
          return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center min-w-[200px]", children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "w-10 h-10 rounded-full border-2 flex items-center justify-center",
                  colors.bg,
                  colors.border,
                  onItemClick && "cursor-pointer hover:scale-110 transition-transform"
                ),
                onClick: () => onItemClick?.(item),
                children: item.icon || /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-current" })
              }
            ),
            !isLast && /* @__PURE__ */ jsx("div", { className: cn("h-0.5 w-full mt-4", colors.line) }),
            /* @__PURE__ */ jsxs("div", { className: "mt-4 text-center", children: [
              /* @__PURE__ */ jsx("h4", { className: "font-medium text-sm text-[var(--color-neutral-900)]", children: item.title }),
              item.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-[var(--color-neutral-600)] mt-1", children: item.description }),
              /* @__PURE__ */ jsx("time", { className: "text-xs text-[var(--color-neutral-500)] mt-2 block", children: formatTimestamp(item.timestamp) })
            ] })
          ] }, item.id);
        })
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-4", className), ...props, children: items.map((item, index) => {
    const colors = variantColors[item.variant || "default"];
    const isLast = index === items.length - 1;
    return /* @__PURE__ */ jsxs("div", { className: "relative flex gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center", children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0",
              colors.bg,
              colors.border,
              onItemClick && "cursor-pointer hover:scale-110 transition-transform"
            ),
            onClick: () => onItemClick?.(item),
            children: item.icon || /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-current" })
          }
        ),
        !isLast && /* @__PURE__ */ jsx("div", { className: cn("w-0.5 flex-1 mt-2", colors.line) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex-1 pb-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("h4", { className: "font-medium text-[var(--color-neutral-900)]", children: item.title }),
            item.description && /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] mt-1", children: item.description })
          ] }),
          /* @__PURE__ */ jsx("time", { className: "text-xs text-[var(--color-neutral-500)] flex-shrink-0", children: formatTimestamp(item.timestamp) })
        ] }),
        item.metadata && Object.keys(item.metadata).length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mt-2", children: Object.entries(item.metadata).map(([key, value]) => /* @__PURE__ */ jsxs(
          "span",
          {
            className: "px-2 py-1 text-xs rounded-[var(--radius-sm)] bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)]",
            children: [
              /* @__PURE__ */ jsxs("strong", { children: [
                key,
                ":"
              ] }),
              " ",
              String(value)
            ]
          },
          key
        )) })
      ] })
    ] }, item.id);
  }) });
}
Timeline.displayName = "Timeline";
function ActivityFeed({
  activities,
  showAvatars = true,
  maxItems,
  onShowMore,
  onActivityClick,
  className,
  ...props
}) {
  const displayedActivities = maxItems ? activities.slice(0, maxItems) : activities;
  const hasMore = maxItems && activities.length > maxItems;
  const formatTimestamp = (timestamp) => {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    const now = /* @__PURE__ */ new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 6e4);
    const diffHours = Math.floor(diffMs / 36e5);
    const diffDays = Math.floor(diffMs / 864e5);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), ...props, children: [
    displayedActivities.map((activity) => /* @__PURE__ */ jsxs(
      "div",
      {
        onClick: () => onActivityClick?.(activity),
        className: cn(
          "flex gap-3 p-3 rounded-[var(--radius-lg)] hover:bg-[var(--color-neutral-50)] transition-colors",
          onActivityClick && "cursor-pointer"
        ),
        children: [
          showAvatars && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: activity.user.avatar ? /* @__PURE__ */ jsx(
            "img",
            {
              src: activity.user.avatar,
              alt: activity.user.name,
              className: "w-10 h-10 rounded-full"
            }
          ) : /* @__PURE__ */ jsx("div", { className: "w-10 h-10 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center font-semibold text-sm", children: activity.user.name.charAt(0).toUpperCase() }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
              /* @__PURE__ */ jsxs("p", { className: "text-sm text-[var(--color-neutral-900)]", children: [
                /* @__PURE__ */ jsx("span", { className: "font-medium", children: activity.user.name }),
                " ",
                /* @__PURE__ */ jsx("span", { className: "text-[var(--color-neutral-600)]", children: activity.action }),
                activity.target && /* @__PURE__ */ jsxs(Fragment, { children: [
                  " ",
                  /* @__PURE__ */ jsx("span", { className: "font-medium", children: activity.target })
                ] })
              ] }),
              /* @__PURE__ */ jsx("time", { className: "text-xs text-[var(--color-neutral-500)] flex-shrink-0", children: formatTimestamp(activity.timestamp) })
            ] }),
            activity.metadata && Object.keys(activity.metadata).length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mt-2", children: Object.entries(activity.metadata).map(([key, value]) => /* @__PURE__ */ jsx(
              "span",
              {
                className: "px-2 py-0.5 text-xs rounded-[var(--radius-sm)] bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)]",
                children: String(value)
              },
              key
            )) })
          ] }),
          activity.icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 text-[var(--color-neutral-400)]", children: activity.icon })
        ]
      },
      activity.id
    )),
    hasMore && onShowMore && /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: onShowMore,
        className: "w-full py-2 text-sm font-medium text-[var(--color-brand-primary)] hover:bg-[var(--color-neutral-50)] rounded-[var(--radius-md)] transition-colors",
        children: [
          "Show More (",
          activities.length - maxItems,
          " more)"
        ]
      }
    ),
    activities.length === 0 && /* @__PURE__ */ jsx("div", { className: "text-center py-8 text-sm text-[var(--color-neutral-600)]", children: "No activities yet" })
  ] });
}
ActivityFeed.displayName = "ActivityFeed";
function DataTableInner({
  data,
  columns,
  loading = false,
  onRowClick,
  emptyMessage = "No data available",
  className,
  ...props
}, ref) {
  const [sortConfig, setSortConfig] = useState(null);
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] border border-[var(--color-neutral-200)]",
        "shadow-[var(--shadow-sm)] overflow-hidden",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", { className: "w-full", children: [
        /* @__PURE__ */ jsx("thead", { className: "bg-[var(--color-neutral-50)] border-b border-[var(--color-neutral-200)]", children: /* @__PURE__ */ jsx("tr", { children: columns.map((column) => /* @__PURE__ */ jsx(
          "th",
          {
            className: cn(
              "px-6 py-3 text-left text-xs font-medium text-[var(--color-neutral-600)] uppercase tracking-wider",
              column.sortable && "cursor-pointer hover:bg-[var(--color-neutral-100)] transition-colors",
              column.width && `w-[${column.width}]`
            ),
            onClick: () => column.sortable && handleSort(column.key),
            children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              column.label,
              column.sortable && sortConfig?.key === column.key && /* @__PURE__ */ jsx("span", { className: "text-[var(--color-brand-primary)]", children: sortConfig.direction === "asc" ? "\u2191" : "\u2193" })
            ] })
          },
          column.key
        )) }) }),
        /* @__PURE__ */ jsx("tbody", { className: "divide-y divide-[var(--color-neutral-200)]", children: loading ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: columns.length, className: "px-6 py-12 text-center", children: /* @__PURE__ */ jsx(Spinner, { size: "lg" }) }) }) : sortedData.length === 0 ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: columns.length, className: "px-6 py-12 text-center text-[var(--color-neutral-600)]", children: emptyMessage }) }) : sortedData.map((row, rowIndex) => /* @__PURE__ */ jsx(
          "tr",
          {
            className: cn(
              "transition-colors",
              onRowClick && "cursor-pointer hover:bg-[var(--color-neutral-50)]"
            ),
            onClick: () => onRowClick?.(row),
            children: columns.map((column) => /* @__PURE__ */ jsx(
              "td",
              {
                className: "px-6 py-4 text-sm text-[var(--color-neutral-900)]",
                children: column.render ? column.render(row) : row[column.key]
              },
              column.key
            ))
          },
          rowIndex
        )) })
      ] }) })
    }
  );
}
var DataTable = forwardRef(DataTableInner);
var Form = forwardRef(
  ({ onSubmit, children, disabled = false, className, ...props }, ref) => {
    const handleSubmit = (event) => {
      event.preventDefault();
      if (!disabled) {
        onSubmit(event);
      }
    };
    return /* @__PURE__ */ jsx(
      "form",
      {
        ref,
        onSubmit: handleSubmit,
        className: cn("space-y-4", className),
        ...props,
        children: /* @__PURE__ */ jsx("fieldset", { disabled, className: "space-y-4", children })
      }
    );
  }
);
Form.displayName = "Form";
var FormField = forwardRef(
  ({
    name,
    label,
    type = "text",
    error,
    helperText,
    required = false,
    render,
    className,
    ...props
  }, ref) => {
    if (render) {
      return /* @__PURE__ */ jsx("div", { className, children: render() });
    }
    if (type === "textarea") {
      return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", className), children: [
        /* @__PURE__ */ jsx(
          "label",
          {
            htmlFor: name,
            className: cn(
              "text-sm font-medium text-[var(--color-neutral-700)]",
              required && 'after:content-["*"] after:ml-0.5 after:text-[var(--color-brand-danger)]'
            ),
            children: label
          }
        ),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            ref,
            id: name,
            name,
            className: cn(
              "w-full px-4 py-2 rounded-[var(--radius-md)]",
              "border border-[var(--color-neutral-300)]",
              "bg-[var(--color-neutral-50)] text-[var(--color-neutral-900)]",
              "transition-colors duration-[var(--transition-duration-base)]",
              "placeholder:text-[var(--color-neutral-400)]",
              "focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-transparent",
              "min-h-[100px] resize-y",
              error && "border-[var(--color-brand-danger)] focus:ring-[var(--color-brand-danger)]",
              "disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed disabled:opacity-50"
            ),
            required,
            "aria-invalid": !!error,
            "aria-describedby": error ? `${name}-error` : helperText ? `${name}-helper` : void 0,
            ...props
          }
        ),
        error && /* @__PURE__ */ jsx(
          "span",
          {
            id: `${name}-error`,
            className: "text-sm text-[var(--color-brand-danger)]",
            role: "alert",
            children: error
          }
        ),
        helperText && !error && /* @__PURE__ */ jsx(
          "span",
          {
            id: `${name}-helper`,
            className: "text-sm text-[var(--color-neutral-600)]",
            children: helperText
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsx(
      Input,
      {
        ref,
        id: name,
        name,
        type,
        label,
        error,
        helperText,
        required,
        className,
        ...props
      }
    );
  }
);
FormField.displayName = "FormField";
var alignmentClasses = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
  between: "justify-between"
};
var FormActions = forwardRef(
  ({ children, align = "right", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex items-center gap-3 pt-4",
          alignmentClasses[align],
          className
        ),
        ...props,
        children
      }
    );
  }
);
FormActions.displayName = "FormActions";
function FormGroup({
  children,
  label,
  helperText,
  error,
  required = false,
  disabled = false,
  orientation = "vertical",
  labelWidth = "200px",
  showRequiredIndicator = true,
  className,
  ...props
}) {
  const isHorizontal = orientation === "horizontal";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "form-group",
        isHorizontal ? "flex items-start gap-4" : "flex flex-col gap-2",
        disabled && "opacity-50 cursor-not-allowed",
        className
      ),
      ...props,
      children: [
        label && /* @__PURE__ */ jsxs(
          "label",
          {
            className: cn(
              "block text-sm font-medium text-[var(--color-neutral-700)]",
              isHorizontal && "pt-2",
              disabled && "cursor-not-allowed"
            ),
            style: isHorizontal ? { width: labelWidth, flexShrink: 0 } : void 0,
            children: [
              label,
              required && showRequiredIndicator && /* @__PURE__ */ jsx("span", { className: "text-[var(--color-brand-danger)] ml-1", "aria-label": "required", children: "*" })
            ]
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: cn("flex-1", isHorizontal && "min-w-0"), children: [
          children,
          (helperText || error) && /* @__PURE__ */ jsx(
            "p",
            {
              className: cn(
                "mt-1.5 text-sm",
                error ? "text-[var(--color-brand-danger)]" : "text-[var(--color-neutral-500)]"
              ),
              children: error || helperText
            }
          )
        ] })
      ]
    }
  );
}
FormGroup.displayName = "FormGroup";
var spacingClasses4 = {
  "2": "gap-2",
  "3": "gap-3",
  "4": "gap-4",
  "6": "gap-6",
  "8": "gap-8"
};
function FormSection({
  children,
  title,
  description,
  icon,
  showDivider = true,
  spacing = "4",
  collapsible = false,
  defaultCollapsed = false,
  className,
  ...props
}) {
  const [isCollapsed, setIsCollapsed] = React2.useState(defaultCollapsed);
  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };
  return /* @__PURE__ */ jsxs("section", { className: cn("form-section", className), ...props, children: [
    /* @__PURE__ */ jsx("div", { className: "mb-6", children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-start gap-3",
          collapsible && "cursor-pointer select-none"
        ),
        onClick: handleToggle,
        role: collapsible ? "button" : void 0,
        "aria-expanded": collapsible ? !isCollapsed : void 0,
        tabIndex: collapsible ? 0 : void 0,
        onKeyDown: collapsible ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        } : void 0,
        children: [
          icon && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 mt-0.5 text-[var(--color-neutral-600)]", children: icon }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
            /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: title }),
            description && /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm text-[var(--color-neutral-600)]", children: description })
          ] }),
          collapsible && /* @__PURE__ */ jsx(
            "svg",
            {
              className: cn(
                "w-5 h-5 flex-shrink-0 transition-transform text-[var(--color-neutral-500)]",
                isCollapsed && "-rotate-90"
              ),
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  strokeWidth: 2,
                  d: "M19 9l-7 7-7-7"
                }
              )
            }
          )
        ]
      }
    ) }),
    (!collapsible || !isCollapsed) && /* @__PURE__ */ jsx("div", { className: cn("flex flex-col", spacingClasses4[spacing]), children }),
    showDivider && /* @__PURE__ */ jsx("div", { className: "mt-8 border-t border-[var(--color-neutral-200)]" })
  ] });
}
FormSection.displayName = "FormSection";
function validateField(value, validation, formData) {
  if (validation.required) {
    const isEmpty = value === void 0 || value === null || value === "" || Array.isArray(value) && value.length === 0;
    if (isEmpty) {
      return typeof validation.required === "string" ? validation.required : "This field is required";
    }
  }
  if (value === void 0 || value === null || value === "") {
    return void 0;
  }
  if (validation.minLength !== void 0) {
    const config = typeof validation.minLength === "number" ? { value: validation.minLength } : validation.minLength;
    if (typeof value === "string" && value.length < config.value) {
      return config.message || `Minimum length is ${config.value} characters`;
    }
  }
  if (validation.maxLength !== void 0) {
    const config = typeof validation.maxLength === "number" ? { value: validation.maxLength } : validation.maxLength;
    if (typeof value === "string" && value.length > config.value) {
      return config.message || `Maximum length is ${config.value} characters`;
    }
  }
  if (validation.min !== void 0) {
    const config = typeof validation.min === "number" ? { value: validation.min } : validation.min;
    if (typeof value === "number" && value < config.value) {
      return config.message || `Minimum value is ${config.value}`;
    }
  }
  if (validation.max !== void 0) {
    const config = typeof validation.max === "number" ? { value: validation.max } : validation.max;
    if (typeof value === "number" && value > config.value) {
      return config.message || `Maximum value is ${config.value}`;
    }
  }
  if (validation.pattern !== void 0) {
    const config = validation.pattern instanceof RegExp ? { value: validation.pattern } : validation.pattern;
    if (typeof value === "string" && !config.value.test(value)) {
      return config.message || "Invalid format";
    }
  }
  if (validation.custom) {
    const rules = Array.isArray(validation.custom) ? validation.custom : [validation.custom];
    for (const rule of rules) {
      const result = rule.validate(value, formData);
      if (result !== true) {
        return result;
      }
    }
  }
  return void 0;
}
function useFormValidation({
  initialValues,
  validationSchema,
  onSubmit,
  validateOnChange = false,
  validateOnBlur = true
}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validateForm = useCallback(
    (formValues = values) => {
      const newErrors = {};
      Object.keys(validationSchema).forEach((fieldName) => {
        const fieldError = validateField(
          formValues[fieldName],
          validationSchema[fieldName],
          formValues
        );
        if (fieldError) {
          newErrors[fieldName] = fieldError;
        }
      });
      return newErrors;
    },
    [validationSchema, values]
  );
  const handleChange = useCallback(
    (name, value) => {
      setValues((prev) => ({ ...prev, [name]: value }));
      if (validateOnChange && validationSchema[name]) {
        const fieldError = validateField(
          value,
          validationSchema[name],
          { ...values, [name]: value }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: fieldError || ""
        }));
      }
    },
    [validateOnChange, validationSchema, values]
  );
  const handleBlur = useCallback(
    (name) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      if (validateOnBlur && validationSchema[name]) {
        const fieldError = validateField(
          values[name],
          validationSchema[name],
          values
        );
        setErrors((prev) => ({
          ...prev,
          [name]: fieldError || ""
        }));
      }
    },
    [validateOnBlur, validationSchema, values]
  );
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      const allTouched = {};
      Object.keys(validationSchema).forEach((key) => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      const formErrors = validateForm();
      setErrors(formErrors);
      if (Object.keys(formErrors).length === 0) {
        setIsSubmitting(true);
        try {
          await onSubmit(values);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [validationSchema, validateForm, onSubmit, values]
  );
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);
  const isValid = Object.keys(validateForm()).length === 0;
  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
    setValues,
    setErrors,
    setTouched
  };
}
var ValidationRules = {
  /**
   * Email validation
   */
  email: (message = "Invalid email address") => ({
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message
    }
  }),
  /**
   * URL validation
   */
  url: (message = "Invalid URL") => ({
    pattern: {
      value: /^https?:\/\/.+/,
      message
    }
  }),
  /**
   * Phone number validation (US format)
   */
  phone: (message = "Invalid phone number") => ({
    pattern: {
      value: /^\+?1?\s*\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/,
      message
    }
  }),
  /**
   * Matches another field (e.g., password confirmation)
   */
  matches: (fieldName, message = `Must match ${fieldName}`) => ({
    custom: {
      validate: (value, formData) => {
        return value === formData?.[fieldName] || message;
      }
    }
  }),
  /**
   * Only alphabetic characters
   */
  alpha: (message = "Only letters allowed") => ({
    pattern: {
      value: /^[a-zA-Z]+$/,
      message
    }
  }),
  /**
   * Only alphanumeric characters
   */
  alphanumeric: (message = "Only letters and numbers allowed") => ({
    pattern: {
      value: /^[a-zA-Z0-9]+$/,
      message
    }
  })
};
var alertVariants = {
  info: {
    container: "bg-[color-mix(in_srgb,var(--color-semantic-info)_12%,transparent)] border-[var(--color-semantic-info)] text-[var(--color-neutral-900)]",
    icon: "text-[var(--color-semantic-info)]",
    title: "text-[var(--color-neutral-900)]"
  },
  success: {
    container: "bg-[color-mix(in_srgb,var(--color-semantic-success)_12%,transparent)] border-[var(--color-semantic-success)] text-[var(--color-neutral-900)]",
    icon: "text-[var(--color-semantic-success)]",
    title: "text-[var(--color-neutral-900)]"
  },
  warning: {
    container: "bg-[color-mix(in_srgb,var(--color-brand-warning)_12%,transparent)] border-[var(--color-brand-warning)] text-[var(--color-neutral-900)]",
    icon: "text-[var(--color-brand-warning)]",
    title: "text-[var(--color-neutral-900)]"
  },
  danger: {
    container: "bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)] border-[var(--color-brand-danger)] text-[var(--color-neutral-900)]",
    icon: "text-[var(--color-brand-danger)]",
    title: "text-[var(--color-neutral-900)]"
  }
};
function Alert({
  variant = "info",
  title,
  children,
  icon,
  dismissible = false,
  onDismiss,
  className,
  ...props
}) {
  const styles = alertVariants[variant];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "relative flex gap-3 p-4 rounded-[var(--radius-lg)] border",
        styles.container,
        className
      ),
      role: "alert",
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("div", { className: cn("flex-shrink-0", styles.icon), children: icon }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
          title && /* @__PURE__ */ jsx("h3", { className: cn("font-semibold mb-1", styles.title), children: title }),
          /* @__PURE__ */ jsx("div", { className: "text-sm", children })
        ] }),
        dismissible && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: cn(
              "flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors",
              styles.icon
            ),
            onClick: onDismiss,
            "aria-label": "Dismiss alert",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                width: "16",
                height: "16",
                viewBox: "0 0 16 16",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M12 4L4 12M4 4L12 12",
                    stroke: "currentColor",
                    strokeWidth: "2",
                    strokeLinecap: "round"
                  }
                )
              }
            )
          }
        )
      ]
    }
  );
}
Alert.displayName = "Alert";
var toastVariants = {
  info: {
    container: "bg-[var(--color-semantic-info)] text-white",
    icon: "text-white"
  },
  success: {
    container: "bg-[var(--color-semantic-success)] text-white",
    icon: "text-white"
  },
  warning: {
    container: "bg-[var(--color-brand-warning)] text-white",
    icon: "text-white"
  },
  danger: {
    container: "bg-[var(--color-brand-danger)] text-white",
    icon: "text-white"
  }
};
var positionClasses4 = {
  "top-left": "top-4 left-4",
  "top-center": "top-4 left-1/2 -translate-x-1/2",
  "top-right": "top-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-4 right-4"
};
function Toast({
  variant = "info",
  title,
  children,
  icon,
  visible = true,
  duration = 5e3,
  onDismiss,
  position = "top-right",
  className,
  ...props
}) {
  const styles = toastVariants[variant];
  useEffect(() => {
    if (visible && duration > 0 && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [visible, duration, onDismiss]);
  if (!visible) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "fixed z-50 flex gap-3 p-4 rounded-[var(--radius-lg)]",
        "shadow-[var(--shadow-xl)]",
        "min-w-[300px] max-w-[500px]",
        "animate-in slide-in-from-top-2 fade-in",
        styles.container,
        positionClasses4[position],
        className
      ),
      role: "alert",
      "aria-live": "polite",
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx("div", { className: cn("flex-shrink-0", styles.icon), children: icon }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
          title && /* @__PURE__ */ jsx("h3", { className: "font-semibold mb-1", children: title }),
          children && /* @__PURE__ */ jsx("div", { className: "text-sm opacity-90", children })
        ] }),
        onDismiss && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors",
            onClick: onDismiss,
            "aria-label": "Dismiss notification",
            children: /* @__PURE__ */ jsx(
              "svg",
              {
                width: "16",
                height: "16",
                viewBox: "0 0 16 16",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M12 4L4 12M4 4L12 12",
                    stroke: "currentColor",
                    strokeWidth: "2",
                    strokeLinecap: "round"
                  }
                )
              }
            )
          }
        )
      ]
    }
  );
}
Toast.displayName = "Toast";
var dotSizes2 = {
  sm: "h-2 w-2",
  md: "h-3 w-3",
  lg: "h-4 w-4"
};
function LoadingState({
  type = "spinner",
  size = "md",
  message,
  centered = true,
  className,
  ...props
}) {
  const renderIndicator = () => {
    switch (type) {
      case "spinner":
        return /* @__PURE__ */ jsx(Spinner, { size });
      case "dots":
        return /* @__PURE__ */ jsx("div", { className: "flex gap-2", children: [0, 1, 2].map((index) => /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "rounded-full bg-[var(--color-brand-primary)]",
              "animate-bounce",
              dotSizes2[size]
            ),
            style: {
              animationDelay: `${index * 150}ms`,
              animationDuration: "600ms"
            }
          },
          index
        )) });
      case "pulse":
        return /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "rounded-full bg-[var(--color-brand-primary)] animate-pulse",
              size === "sm" && "h-8 w-8",
              size === "md" && "h-12 w-12",
              size === "lg" && "h-16 w-16"
            )
          }
        );
      default:
        return /* @__PURE__ */ jsx(Spinner, { size });
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col items-center gap-3",
        centered && "justify-center min-h-[200px]",
        className
      ),
      role: "status",
      "aria-live": "polite",
      ...props,
      children: [
        renderIndicator(),
        message && /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)]", children: message }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Loading..." })
      ]
    }
  );
}
LoadingState.displayName = "LoadingState";
var titleSizes = {
  sm: "text-base",
  md: "text-lg",
  lg: "text-xl"
};
var descriptionSizes = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base"
};
var iconSizes = {
  sm: "text-4xl",
  md: "text-6xl",
  lg: "text-8xl"
};
function EmptyState({
  icon,
  title,
  description,
  action,
  size = "md",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center text-center",
        "py-12 px-4",
        className
      ),
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "text-[var(--color-neutral-400)] mb-4",
              iconSizes[size]
            ),
            children: icon
          }
        ),
        /* @__PURE__ */ jsx(
          "h3",
          {
            className: cn(
              "font-semibold text-[var(--color-neutral-900)] mb-2",
              titleSizes[size]
            ),
            children: title
          }
        ),
        description && /* @__PURE__ */ jsx(
          "p",
          {
            className: cn(
              "text-[var(--color-neutral-600)] mb-6 max-w-md",
              descriptionSizes[size]
            ),
            children: description
          }
        ),
        action && /* @__PURE__ */ jsx("div", { children: action })
      ]
    }
  );
}
EmptyState.displayName = "EmptyState";
function ConfirmDialog({
  open,
  onClose,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "primary",
  onConfirm,
  icon,
  showCancel = true
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose]);
  if (!open) return null;
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 bg-black/50 z-40 animate-in fade-in",
        onClick: onClose,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: /* @__PURE__ */ jsx(
      "div",
      {
        role: "alertdialog",
        "aria-modal": "true",
        "aria-labelledby": "confirm-dialog-title",
        "aria-describedby": "confirm-dialog-message",
        className: cn(
          "w-full max-w-md bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)]",
          "animate-in fade-in zoom-in-95"
        ),
        onClick: (e) => e.stopPropagation(),
        children: /* @__PURE__ */ jsxs("div", { className: "p-6", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex gap-4 mb-4", children: [
            icon && /* @__PURE__ */ jsx("div", { className: cn(
              "flex-shrink-0",
              variant === "danger" ? "text-[var(--color-brand-danger)]" : "text-[var(--color-brand-primary)]"
            ), children: icon }),
            /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("h2", { id: "confirm-dialog-title", className: "text-lg font-semibold text-[var(--color-neutral-900)]", children: title }) })
          ] }),
          /* @__PURE__ */ jsx("div", { id: "confirm-dialog-message", className: "text-sm text-[var(--color-neutral-600)] mb-6", children: message }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-3", children: [
            showCancel && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                onClick: onClose,
                children: cancelText
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant,
                onClick: handleConfirm,
                autoFocus: true,
                children: confirmText
              }
            )
          ] })
        ] })
      }
    ) })
  ] });
}
ConfirmDialog.displayName = "ConfirmDialog";
function Prompt({
  open,
  onClose,
  title,
  message,
  inputLabel,
  placeholder,
  defaultValue = "",
  submitText = "Submit",
  cancelText = "Cancel",
  onSubmit,
  validate,
  inputType = "text"
}) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState();
  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(void 0);
    }
  }, [open, defaultValue]);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose]);
  if (!open) return null;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate) {
      const validationError = validate(value);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    onSubmit(value);
    onClose();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 bg-black/50 z-40 animate-in fade-in",
        onClick: onClose,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: /* @__PURE__ */ jsx(
      "div",
      {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "prompt-dialog-title",
        className: cn(
          "w-full max-w-md bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)]",
          "animate-in fade-in zoom-in-95"
        ),
        onClick: (e) => e.stopPropagation(),
        children: /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, children: [
          /* @__PURE__ */ jsxs("div", { className: "p-6", children: [
            /* @__PURE__ */ jsx("h2", { id: "prompt-dialog-title", className: "text-lg font-semibold text-[var(--color-neutral-900)] mb-4", children: title }),
            message && /* @__PURE__ */ jsx("div", { className: "text-sm text-[var(--color-neutral-600)] mb-4", children: message }),
            /* @__PURE__ */ jsx(
              Input,
              {
                label: inputLabel,
                type: inputType,
                value,
                onChange: (e) => {
                  setValue(e.target.value);
                  setError(void 0);
                },
                placeholder,
                error,
                autoFocus: true,
                fullWidth: true
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "px-6 py-4 border-t border-[var(--color-neutral-200)] flex items-center justify-end gap-3", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "button",
                variant: "ghost",
                onClick: onClose,
                children: cancelText
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                type: "submit",
                variant: "primary",
                children: submitText
              }
            )
          ] })
        ] })
      }
    ) })
  ] });
}
Prompt.displayName = "Prompt";
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    __publicField(this, "handleReset", () => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    });
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    this.props.onError?.(error, errorInfo);
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }
  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails = process.env.NODE_ENV === "development" } = this.props;
    if (hasError && error) {
      if (fallback) {
        if (typeof fallback === "function") {
          return fallback(error, errorInfo);
        }
        return fallback;
      }
      return /* @__PURE__ */ jsx("div", { className: "min-h-screen flex items-center justify-center p-4 bg-[var(--color-neutral-50)]", children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-2xl bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] p-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 mb-4", children: [
          /* @__PURE__ */ jsx(
            "svg",
            {
              className: "w-12 h-12 text-[var(--color-brand-danger)] flex-shrink-0",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  strokeWidth: 2,
                  d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                }
              )
            }
          ),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("h1", { className: "text-2xl font-bold text-[var(--color-neutral-900)]", children: "Something went wrong" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)] mt-1", children: "An unexpected error occurred. Please try again." })
          ] })
        ] }),
        showDetails && /* @__PURE__ */ jsx("div", { className: "mb-6", children: /* @__PURE__ */ jsxs("details", { className: "mt-4", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-sm font-medium text-[var(--color-neutral-700)] hover:text-[var(--color-neutral-900)]", children: "Error Details" }),
          /* @__PURE__ */ jsxs("div", { className: "mt-4 p-4 bg-[color-mix(in_srgb,var(--color-brand-danger)_12%,transparent)] border border-[var(--color-brand-danger)] rounded-[var(--radius-md)] overflow-auto", children: [
            /* @__PURE__ */ jsxs("p", { className: "text-sm font-mono text-[var(--color-brand-danger)] mb-2", children: [
              /* @__PURE__ */ jsxs("strong", { children: [
                error.name,
                ":"
              ] }),
              " ",
              error.message
            ] }),
            error.stack && /* @__PURE__ */ jsx("pre", { className: "text-xs font-mono text-[var(--color-brand-danger)] whitespace-pre-wrap", children: error.stack }),
            errorInfo && errorInfo.componentStack && /* @__PURE__ */ jsx("pre", { className: "text-xs font-mono text-[var(--color-brand-danger)] whitespace-pre-wrap mt-4", children: errorInfo.componentStack })
          ] })
        ] }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-3", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: this.handleReset,
              className: "px-4 py-2 rounded-[var(--radius-md)] bg-[var(--color-brand-primary)] text-white hover:opacity-90 transition-opacity font-medium",
              children: "Try Again"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => window.location.reload(),
              className: "px-4 py-2 rounded-[var(--radius-md)] border border-[var(--color-neutral-300)] text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-100)] transition-colors font-medium",
              children: "Reload Page"
            }
          )
        ] })
      ] }) });
    }
    return children;
  }
};
var colsClasses = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
  7: "grid-cols-7",
  8: "grid-cols-8",
  9: "grid-cols-9",
  10: "grid-cols-10",
  11: "grid-cols-11",
  12: "grid-cols-12"
};
var gapClasses = {
  "0": "gap-0",
  "1": "gap-1",
  "2": "gap-2",
  "3": "gap-3",
  "4": "gap-4",
  "5": "gap-5",
  "6": "gap-6",
  "8": "gap-8",
  "10": "gap-10",
  "12": "gap-12"
};
var Grid = forwardRef(
  ({ cols = 12, gap = "4", responsive, children, className, ...props }, ref) => {
    const responsiveClasses = responsive ? [
      responsive.sm && `sm:grid-cols-${responsive.sm}`,
      responsive.md && `md:grid-cols-${responsive.md}`,
      responsive.lg && `lg:grid-cols-${responsive.lg}`,
      responsive.xl && `xl:grid-cols-${responsive.xl}`
    ].filter(Boolean).join(" ") : "";
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "grid",
          colsClasses[cols],
          gapClasses[gap],
          responsiveClasses,
          className
        ),
        ...props,
        children
      }
    );
  }
);
Grid.displayName = "Grid";
var directionClasses = {
  row: "flex-row",
  column: "flex-col",
  "row-reverse": "flex-row-reverse",
  "column-reverse": "flex-col-reverse"
};
var alignClasses2 = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline"
};
var justifyClasses = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly"
};
var gapClasses2 = {
  "0": "gap-0",
  "1": "gap-1",
  "2": "gap-2",
  "3": "gap-3",
  "4": "gap-4",
  "5": "gap-5",
  "6": "gap-6",
  "8": "gap-8",
  "10": "gap-10",
  "12": "gap-12"
};
var wrapClasses = {
  nowrap: "flex-nowrap",
  wrap: "flex-wrap",
  "wrap-reverse": "flex-wrap-reverse"
};
var Flex = forwardRef(
  ({
    direction = "row",
    align = "stretch",
    justify = "start",
    gap = "0",
    wrap = "nowrap",
    children,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex",
          directionClasses[direction],
          alignClasses2[align],
          justifyClasses[justify],
          gapClasses2[gap],
          wrapClasses[wrap],
          className
        ),
        ...props,
        children
      }
    );
  }
);
Flex.displayName = "Flex";
var spacingClasses5 = {
  "0": "gap-0",
  "1": "gap-1",
  "2": "gap-2",
  "3": "gap-3",
  "4": "gap-4",
  "5": "gap-5",
  "6": "gap-6",
  "8": "gap-8",
  "10": "gap-10",
  "12": "gap-12"
};
var alignClasses3 = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch"
};
var Stack = forwardRef(
  ({
    direction = "vertical",
    spacing = "4",
    align = "stretch",
    divider = false,
    children,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex",
          direction === "vertical" ? "flex-col" : "flex-row",
          spacingClasses5[spacing],
          alignClasses3[align],
          divider && (direction === "vertical" ? "divide-y divide-[var(--color-neutral-200)]" : "divide-x divide-[var(--color-neutral-200)]"),
          className
        ),
        ...props,
        children
      }
    );
  }
);
Stack.displayName = "Stack";
var maxWidthClasses = {
  sm: "max-w-screen-sm",
  md: "max-w-screen-md",
  lg: "max-w-screen-lg",
  xl: "max-w-screen-xl",
  "2xl": "max-w-screen-2xl",
  full: "max-w-full"
};
var paddingClasses = {
  "0": "px-0",
  "2": "px-2",
  "4": "px-4",
  "6": "px-6",
  "8": "px-8"
};
var Container = forwardRef(
  ({
    maxWidth = "lg",
    padding = "4",
    center = true,
    children,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "w-full",
          maxWidthClasses[maxWidth],
          paddingClasses[padding],
          center && "mx-auto",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Container.displayName = "Container";
var PageLayout = forwardRef(
  ({
    header,
    sidebar,
    footer,
    children,
    sidebarPosition = "left",
    fullWidth = false,
    className,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("min-h-screen flex flex-col", className),
        ...props,
        children: [
          header && /* @__PURE__ */ jsx("header", { className: "flex-shrink-0 border-b border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)]", children: header }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 flex overflow-hidden", children: [
            sidebar && sidebarPosition === "left" && /* @__PURE__ */ jsx("aside", { className: "flex-shrink-0 w-64 border-r border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)] overflow-y-auto", children: sidebar }),
            /* @__PURE__ */ jsx(
              "main",
              {
                className: cn(
                  "flex-1 overflow-y-auto bg-[var(--color-neutral-50)]",
                  !fullWidth && "p-6"
                ),
                children
              }
            ),
            sidebar && sidebarPosition === "right" && /* @__PURE__ */ jsx("aside", { className: "flex-shrink-0 w-64 border-l border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)] overflow-y-auto", children: sidebar })
          ] }),
          footer && /* @__PURE__ */ jsx("footer", { className: "flex-shrink-0 border-t border-[var(--color-neutral-200)] bg-[var(--color-neutral-100)]", children: footer })
        ]
      }
    );
  }
);
PageLayout.displayName = "PageLayout";
var spacingClasses6 = {
  none: "py-0",
  sm: "py-4",
  md: "py-8",
  lg: "py-12",
  xl: "py-16"
};
var backgroundClasses = {
  transparent: "",
  neutral: "bg-[var(--color-neutral-50)]",
  white: "bg-[var(--color-neutral-100)]"
};
var Section = forwardRef(
  ({ spacing = "md", background = "transparent", children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "section",
      {
        ref,
        className: cn(
          spacingClasses6[spacing],
          backgroundClasses[background],
          className
        ),
        ...props,
        children
      }
    );
  }
);
Section.displayName = "Section";
var sizeClasses6 = {
  vertical: {
    "1": "h-1",
    "2": "h-2",
    "3": "h-3",
    "4": "h-4",
    "5": "h-5",
    "6": "h-6",
    "8": "h-8",
    "10": "h-10",
    "12": "h-12",
    "16": "h-16",
    "20": "h-20",
    "24": "h-24"
  },
  horizontal: {
    "1": "w-1",
    "2": "w-2",
    "3": "w-3",
    "4": "w-4",
    "5": "w-5",
    "6": "w-6",
    "8": "w-8",
    "10": "w-10",
    "12": "w-12",
    "16": "w-16",
    "20": "w-20",
    "24": "w-24"
  }
};
function Spacer({
  size = "4",
  orientation = "vertical",
  grow = false,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "spacer",
        !grow && sizeClasses6[orientation][size],
        grow && (orientation === "vertical" ? "flex-1" : "flex-1"),
        className
      ),
      "aria-hidden": "true",
      ...props
    }
  );
}
Spacer.displayName = "Spacer";
var spacingMap = {
  "0": "0",
  "1": "1",
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "8": "8",
  "10": "10",
  "12": "12",
  "16": "16"
};
var displayMap = {
  block: "block",
  "inline-block": "inline-block",
  flex: "flex",
  "inline-flex": "inline-flex",
  grid: "grid",
  "inline-grid": "inline-grid",
  none: "hidden"
};
var positionMap = {
  static: "static",
  relative: "relative",
  absolute: "absolute",
  fixed: "fixed",
  sticky: "sticky"
};
var roundedMap = {
  none: "rounded-none",
  sm: "rounded-[var(--radius-sm)]",
  md: "rounded-[var(--radius-md)]",
  lg: "rounded-[var(--radius-lg)]",
  full: "rounded-full"
};
var shadowMap = {
  none: "shadow-none",
  sm: "shadow-[var(--shadow-sm)]",
  md: "shadow-[var(--shadow-md)]",
  lg: "shadow-[var(--shadow-lg)]",
  xl: "shadow-[var(--shadow-xl)]"
};
function Box({
  as,
  children,
  p,
  px,
  py,
  pt,
  pr,
  pb,
  pl,
  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,
  w,
  h,
  display,
  position,
  bg,
  rounded,
  shadow,
  className,
  ...props
}) {
  const Component2 = as || "div";
  const classes = cn(
    // Padding
    p && `p-${spacingMap[p]}`,
    px && `px-${spacingMap[px]}`,
    py && `py-${spacingMap[py]}`,
    pt && `pt-${spacingMap[pt]}`,
    pr && `pr-${spacingMap[pr]}`,
    pb && `pb-${spacingMap[pb]}`,
    pl && `pl-${spacingMap[pl]}`,
    // Margin
    m && `m-${spacingMap[m]}`,
    mx && `mx-${spacingMap[mx]}`,
    my && `my-${spacingMap[my]}`,
    mt && `mt-${spacingMap[mt]}`,
    mr && `mr-${spacingMap[mr]}`,
    mb && `mb-${spacingMap[mb]}`,
    ml && `ml-${spacingMap[ml]}`,
    // Width
    w === "full" && "w-full",
    w === "screen" && "w-screen",
    w === "auto" && "w-auto",
    w && spacingMap[w] && `w-${spacingMap[w]}`,
    // Height
    h === "full" && "h-full",
    h === "screen" && "h-screen",
    h === "auto" && "h-auto",
    h && spacingMap[h] && `h-${spacingMap[h]}`,
    // Display
    display && displayMap[display],
    // Position
    position && positionMap[position],
    // Rounded
    rounded && roundedMap[rounded],
    // Shadow
    shadow && shadowMap[shadow],
    className
  );
  const style = bg ? { backgroundColor: bg, ...props.style } : props.style;
  return /* @__PURE__ */ jsx(Component2, { className: classes, style, ...props, children });
}
Box.displayName = "Box";
function Center({
  children,
  horizontal = false,
  vertical = false,
  inline = false,
  minHeight,
  className,
  style,
  ...props
}) {
  const centerBoth = !horizontal && !vertical;
  const shouldCenterH = horizontal || centerBoth;
  const shouldCenterV = vertical || centerBoth;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        inline ? "inline-flex" : "flex",
        shouldCenterH && "justify-center",
        shouldCenterV && "items-center",
        className
      ),
      style: {
        minHeight,
        ...style
      },
      ...props,
      children
    }
  );
}
Center.displayName = "Center";
var presetRatios = {
  square: 1,
  // 1:1
  video: 16 / 9,
  // 16:9
  widescreen: 16 / 9,
  // 16:9
  ultrawide: 21 / 9,
  // 21:9
  portrait: 3 / 4,
  // 3:4
  photo: 3 / 2
  // 3:2
};
function AspectRatio({
  children,
  ratio: ratioProp,
  preset,
  className,
  style,
  ...props
}) {
  const ratio = preset ? presetRatios[preset] : ratioProp;
  const paddingBottom = `${1 / ratio * 100}%`;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("relative w-full", className),
      style: {
        paddingBottom,
        ...style
      },
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: "absolute inset-0", children })
    }
  );
}
AspectRatio.displayName = "AspectRatio";
function ThemeSwitcher({ className }) {
  const { themeId, availableThemes, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const themes = Object.values(availableThemes);
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => setIsOpen(!isOpen),
        className: "px-4 py-2 bg-[var(--color-neutral-100)] hover:bg-[var(--color-neutral-200)] rounded-[var(--radius-md)] font-medium transition-colors flex items-center gap-2",
        children: [
          /* @__PURE__ */ jsx("span", { children: "\u{1F3A8}" }),
          /* @__PURE__ */ jsxs("span", { children: [
            "Theme: ",
            availableThemes[themeId]?.name
          ] })
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "fixed inset-0 bg-black/50 z-40",
          onClick: () => setIsOpen(false)
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs("div", { className: "bg-[var(--color-neutral-100)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center mb-6", children: [
          /* @__PURE__ */ jsx("h2", { className: "text-2xl font-bold", children: "Choose Theme" }),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => setIsOpen(false),
              className: "w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--color-neutral-100)] transition-colors",
              "aria-label": "Close",
              children: "\u2715"
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", children: themes.map((theme) => /* @__PURE__ */ jsx(
          ThemePreview,
          {
            theme,
            isSelected: theme.id === themeId,
            onClick: () => {
              setTheme(theme.id);
              setIsOpen(false);
            }
          },
          theme.id
        )) })
      ] }) })
    ] })
  ] });
}
function ThemePreview({ theme, isSelected, onClick }) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      onClick,
      className: `
        relative p-4 rounded-[var(--radius-lg)] border-2 transition-all
        hover:scale-105 hover:shadow-[var(--shadow-md)]
        text-left w-full
        ${isSelected ? "border-[var(--color-brand-primary)] shadow-[var(--shadow-md)]" : "border-[var(--color-neutral-200)] hover:border-[var(--color-neutral-300)]"}
      `,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-3", children: [
          /* @__PURE__ */ jsx("span", { className: "text-2xl", children: theme.icon }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: theme.name }),
            /* @__PURE__ */ jsx("p", { className: "text-sm text-[var(--color-neutral-600)]", children: theme.description })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex gap-2 mb-3", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-8 h-8 rounded-full border border-[var(--color-neutral-300)]",
              style: { backgroundColor: theme.colors.brand.primary },
              title: "Primary"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-8 h-8 rounded-full border border-[var(--color-neutral-300)]",
              style: { backgroundColor: theme.colors.brand.secondary },
              title: "Secondary"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-8 h-8 rounded-full border border-[var(--color-neutral-300)]",
              style: { backgroundColor: theme.colors.semantic.success },
              title: "Success"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-8 h-8 rounded-full border border-[var(--color-neutral-300)]",
              style: { backgroundColor: theme.colors.semantic.warning },
              title: "Warning"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "w-8 h-8 rounded-full border border-[var(--color-neutral-300)]",
              style: { backgroundColor: theme.colors.semantic.error },
              title: "Error"
            }
          )
        ] }),
        isSelected && /* @__PURE__ */ jsx("div", { className: "absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--color-brand-primary)] text-white flex items-center justify-center text-sm", children: "\u2713" })
      ]
    }
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, ActivityFeed, AgentCard, AgentList, AgentStatus, Alert, AspectRatio, Avatar, AvatarGroup, Badge, Box, Button, ButtonGroup, Card, CardBody, CardFooter, CardHeader, Center, ChatBubble, ChatInput, ChatThread, Checkbox, ConfirmDialog, Container, DataTable, Dialog, Divider, Drawer, Dropdown, DropdownItem, DropdownSeparator, EmptyState, ErrorBoundary, Flex, Form, FormActions, FormField, FormGroup, FormSection, Grid, IconButton, Input, LoadingState, Menu, Modal, ModalFooter, PageLayout, Popover, ProgressBar, Prompt, Radio, Section, Select, Skeleton, Spacer, Spinner, Stack, StatsCard, Switch, Tabs, TabsContent, TabsList, TabsTrigger, Tag, TextArea, ThemeSwitcher, Timeline, Toast, Tooltip, ValidationRules, WorkflowCard, cn, useFormValidation };
